import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import LayoutComponent from '../BaseScreen'
import { useIntake } from '../../Context/IntakeContext'
import { useData } from '../../Context/DataContext'
import ChartComponent from '../../Components/Analytics/ChartComponent';
import SelectOptionComponent from '../../Components/Tables/SelectOptionComponent';
import { SkeletonBlock } from '../../Components/Loaders/Loader';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Rectangle,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  ComposedChart,
  Area,
  ReferenceLine
} from "recharts";
import axios from 'axios';
import { ChevronsDown, ChevronsUp, Phone, PhoneCall, PhoneMissed, PhoneOutgoing } from 'react-feather';
import { usePlaidLink } from 'react-plaid-link';
import DisplayAccordianHeader from '../../Components/SortAndFilter/DisplayAccordianHeader';
import baseUrl from '../../Utils/baseUrl';
import CalendarSelectComponent from '../../Components/Inputs/CalendarSelectComponent';
import ButtonComponent from '../../Components/Inputs/ButtonComponent';
import TotalsPills from '../../Components/Tables/TotalsPills';
import { DateUtilityFunctions } from '../../Utils/DateUtilityFunctions';
import { useAuth } from '../../Context/AuthContext';
import LoadingScreen from '../LoadingScreen';
import GeoAnalyticsSection from '../GeoAnalytics/GeoAnalyticsSection';

const ANALYTICS_CACHE_KEY = 'intellasure:analytics-cache:v1'
const analyticsCache = new Map<string, any>()

const VENDOR_DOMAIN_OVERRIDES: Record<string, string> = {
  'amazon web services': 'aws.amazon.com',
  'aws': 'aws.amazon.com',
  'american express': 'americanexpress.com',
  'ringcentral': 'ringcentral.com',
  'paychex': 'paychex.com',
}

const vendorDomain = (name: string): string => {
  const key = (name || '').toLowerCase().trim()
  return VENDOR_DOMAIN_OVERRIDES[key] || key.replace(/\s+/g, '') + '.com'
}

try {
  const raw = sessionStorage.getItem(ANALYTICS_CACHE_KEY)
  if (raw) {
    const parsed = JSON.parse(raw) as Array<[string, any]>
    parsed.forEach(([k, v]) => analyticsCache.set(k, v))
  }
} catch {
  // sessionStorage disabled / quota / parse error — safe to ignore
}

let _persistPending = false
const persistAnalyticsCache = () => {
  if (_persistPending) return
  _persistPending = true
  setTimeout(() => {
    _persistPending = false
    try {
      sessionStorage.setItem(
        ANALYTICS_CACHE_KEY,
        JSON.stringify(Array.from(analyticsCache.entries()))
      )
    } catch {
      // ignore — quota / privacy mode
    }
  }, 250)
}

interface IntakeProps {
  active: boolean;
  booked: string;
  checked_in: string;
  coordinator: string;
  created_date: string;
  date: string;
  date_of_birth: string;
  in_network_oop: number;
  inn_deductable: number;
  insurance: string;
  name: string;
  onn_deducatible: number;
  out_network_oop: number;
  payer_id: string;
  policy_id: string;
  prefix: string;
  source: string;
  summary_out: string;
}

interface AnalyticsDatum {
  clients: number;
  date: string;
  Arrived: number;
  MIA: number;
  BD: number;
  'Pending Arrival': number;
  Approved: number;
  Denied: number;
  'Private Pay': number;
  'Approved No RTC': number;
  'Approved LB No RTC': number;
  'Pending Approval': number;
  'Approved DB': number;
  'Total Approved': number;
  waiting_arrival: number;
  Booked: number;
  'Incoming Clients': number;
  arrived_approved_percent: number;
  approved_total_percent: number;
  arrived_total_percent: number;
}

interface KeyLabelPair {
  key: string;
  label: string;
}

const formatDollar = (val: number): string => {
  const abs = Math.abs(val)
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `$${Math.round(abs / 1000).toLocaleString()}k`
  return `$${Math.round(abs).toLocaleString()}`
}

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sublabel?: string;
  change?: number;
  changeLabel?: string;
  changeType?: 'number' | 'percent' | 'currency';
  inverseColors?: boolean;
  tooltip?: string;
  notice?: string;
}> = ({ label, value, sublabel, change, changeLabel, changeType = 'number', inverseColors, tooltip, notice }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const isPositive = change !== undefined && change >= 0
  const colorClass = inverseColors
    ? (isPositive ? 'text-red-500' : 'text-green-500')
    : (isPositive ? 'text-green-500' : 'text-red-500')

  const formatChange = (val: number) => {
    const sign = val >= 0 ? '+' : ''
    if (changeType === 'percent') return `${sign}${val}%`
    if (changeType === 'currency') {
      return `${val >= 0 ? '+' : '-'}${formatDollar(Math.abs(val))}`
    }
    return `${sign}${val}`
  }

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl p-5 shadow-sm hover:bg-dark-card-hover transition-colors duration-200 relative">
      {notice && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-dark-card/60 backdrop-blur-sm">
          <span className="text-xs text-yellow-400 font-medium">{notice}</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-400">{label}</div>
        {tooltip && (
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            {showTooltip && (
              <div className="absolute right-0 top-6 z-50 w-56 p-2.5 text-xs text-gray-200 bg-gray-800 border border-gray-600 rounded-lg shadow-xl leading-relaxed">
                {tooltip}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-3xl font-semibold text-accent-teal">{value}</span>
        {change !== undefined && (
          <span className={`flex items-center text-sm font-medium ${colorClass}`}>
            {isPositive ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
            {formatChange(change)}
          </span>
        )}
      </div>
      {sublabel ? <div className="mt-1 text-xs text-gray-500">{sublabel}</div> : null}
      {changeLabel ? <div className="mt-0.5 text-xs text-gray-600">{changeLabel}</div> : null}
    </div>
  );
};

const SummaryTable: React.FC<{
  title: string;
  data: any[];
  keysAndLabels: KeyLabelPair[];
}> = ({ title, data, keysAndLabels }) => {
  const rows: any[] = Array.isArray(data) ? data : [];
  const calcTotal = (key: string) => {
    return rows.reduce((acc: number, obj: any) => {
      const v = obj?.[key];
      const n = typeof v === 'number' ? v : parseInt(v ?? '0', 10);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
  };

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="text-white font-semibold">{title}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-dark-border">
              <th className="py-2 pr-4 font-medium">Metric</th>
              <th className="py-2 pr-4 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {keysAndLabels.map(({ key, label }) => (
              <tr key={key} className="border-b border-dark-border">
                <td className="py-2 pr-4 text-white">{label}</td>
                <td className="py-2 pr-4 text-accent-teal">{calcTotal(key)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const keysAndLabelsCoordinators = [
  {key: 'value',label: 'Dylan Finch'},
  {key: 'Whitney  Meeder',label: 'Whitney M.'},
  {key: 'Cynthia Chacon',label: 'Cynthia C.'},
  {key: 'Branden  Cox ',label: 'Branden C.'},
  {key: 'randi Hamilton',label: 'Randi H.'},
  {key: 'Kelly Mahaffey',label: 'Kelly M.'},
  {key: 'Kevyn Howard',label: 'Kevyn H.'},
  {key: 'MICHAEL CROTHERS', label: 'Michael C.'}
]

const keysAndLabelsOverall = [
  { key: 'Booked', label: 'Booked' },
  { key: 'Denied', label: 'Denied' },
  { key: 'Pending', label: 'Pending' },
  { key: 'Total', label: 'Total' }
];

const IntakeAnalytivsScreen = () => {

  const {allUsers, currentProfile} = useAuth()
  const {currentTimeZone} = useData()
  const {intakeRecords} = useIntake()
  const {timespan, fetchAnticipatedData, anticipatedDischargeData} = useIntake()
  const [fetchingData, setFetchingData] = useState<boolean>(false)

  const statusOPtions = [
    'Pending Approval',
    'Total Approved',
    'Denied',
    'Private Pay'
  ]

  const approvedOptions = [
    'Approved',
    'Approved No RTC',
    'Approved LB No RTC',
    'Approved DB'
  ]

  const checkedInOptions = [
    'Arrived',
    'Pending Arrival',
    'MIA',
    'BD'
  ]

  useEffect(() => {
    const fetchData = async () => {
      try {
        processIntakeInformation(intakeRecords);
        await grabCensusData();
        await grabIntakeConversionData();
        await fetchAnticipatedData();
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      }
    };
    fetchData();
  }, [])


  const currentDate = new Date()
  const oneWeekAgo = new Date(currentDate)
  oneWeekAgo.setDate(currentDate.getDate() - 7);
  const oneMonthAgo = new Date(currentDate)
  oneMonthAgo.setDate(currentDate.getDate() - 31);
  

  const [analyticsData, setAnalyticsData] = useState<any>([])
  const [censusData, setCensusData] = useState<any[]>([])
  const [conversionData, setConversionData] = useState<any>([])
  const [pieBookedData, setPieBookedData] = useState<any>([])
  const [pieApprovedData, setPieApprovedData] = useState<any>([])
  const [overallPieData, setOverallPieData] = useState<any>([])
  const [pieStartDate, setPieStartDate] = useState<Date>(oneWeekAgo)
  const [pieEndDate, setPieEndDate] = useState<Date>(currentDate)
  const [statusStartDate, setStatusStartDate] = useState<Date>(oneMonthAgo)
  const [statusEndDate, setStatusEndDate] = useState<Date>(currentDate)

  const [selectedStatus, setSelectedStatus] = useState<string>('Pending Approval')
  const [selectedApproved, setSelectedApproved] = useState<string>('Approved')
  const [selectedCheckedIn, setSelectedCheckedIn] = useState<string>('Arrived')

  const [showStatus, setShowStatus] = useState<boolean>(false)
  const [showApproved, setShowApproved] = useState<boolean>(false)
  const [showCheckedIn, setShowCheckedIn] = useState<boolean>(false)
  const [showCensus, setShowCensus] = useState<boolean>(false)
  const [showConversion, setShowConversion] = useState<boolean>(false)
  const [showAnticipated, setShowAnticipated] = useState<boolean>(false)
  const [dashboardRange, setDashboardRange] = useState<string>('Last 7 days')
  const _today = new Date()
  const _sevenDaysAgo = new Date(_today.getTime() - 6 * 86400000)
  const [dashboardStartDate, setDashboardStartDate] = useState<Date>(_sevenDaysAgo)
  const [dashboardEndDate, setDashboardEndDate] = useState<Date>(_today)
  const [breakdownTab, setBreakdownTab] = useState<'Facilities' | 'Performance & Calls' | 'Leads' | 'Ring Group' | 'Census'>('Facilities')
  const [mainTab, setMainTab] = useState<'Calls & Intake' | 'Deposits' | 'Expenses' | 'Census' | 'Revenue Forecast' | 'Marketing'>('Calls & Intake')
  const [depositData, setDepositData] = useState<any>(null)
  const [depositPreviousPeriod, setDepositPreviousPeriod] = useState<any>(null)
  const [expenseData, setExpenseData] = useState<any>(null)

  const [bankBalance, setBankBalance] = useState<number>(0)
  const [bankBalanceLoading, setBankBalanceLoading] = useState<boolean>(false)
  const [censusAnalytics, setCensusAnalytics] = useState<any>(null)
  const [dcReasonFilter, setDcReasonFilter] = useState<string>('All')
  const [showDcDetail, setShowDcDetail] = useState<boolean>(false)
  const [censusClaims, setCensusClaims] = useState<any>(null)
  const [censusClaimsLoading, setCensusClaimsLoading] = useState<boolean>(false)
  const [revenueData, setRevenueData] = useState<any>(null)
  const [revenueLoading, setRevenueLoading] = useState<boolean>(false)
  const [depositLoading, setDepositLoading] = useState<boolean>(false)
  const [expenseLoading, setExpenseLoading] = useState<boolean>(false)
  const [censusLoading, setCensusLoading] = useState<boolean>(false)
  const [crmDashboard, setCrmDashboard] = useState<any>(null)
  const [crmPreviousPeriod, setCrmPreviousPeriod] = useState<any>(null)
  const [crmLeads, setCrmLeads] = useState<any[]>([])
  const [crmLoading, setCrmLoading] = useState<boolean>(true)
  const [crmError, setCrmError] = useState<string | null>(null)
  const [selectedPeakDay, setSelectedPeakDay] = useState<string | null>(null)
  const [selectedDayLeads, setSelectedDayLeads] = useState<any[]>([])
  const [hiddenCallBars, setHiddenCallBars] = useState<Set<string>>(new Set())
  const [selectedDayLoading, setSelectedDayLoading] = useState<boolean>(false)
  const [showAllCategories, setShowAllCategories] = useState<boolean>(false)
  const [showAllVendors, setShowAllVendors] = useState<boolean>(false)

  // Plaid Link state
  const [plaidLinked, setPlaidLinked] = useState<boolean>(false)
  const [linkToken, setLinkToken] = useState<string | null>(null)

  // Call data state
  const [callData, setCallData] = useState<any>(null)
  const [callPreviousPeriod, setCallPreviousPeriod] = useState<any>(null)
  const [callLoading, setCallLoading] = useState<boolean>(false)
  const [callError, setCallError] = useState<string | null>(null)
  const [callYesterdayFallback, setCallYesterdayFallback] = useState<boolean>(false)

  const normalizeLeadStage = (stage: any) => {
    const s = String(stage ?? '').trim()
    if (!s) return '-'
    if (s.toLowerCase() === 'lost') return 'Denied'
    return s
  }
  
  
  const keysAndLabelsStatus = [
      { key: 'Pending Approval', label: 'Pending Approval' },
      { key: 'Total Approved', label: 'Total Approved' },
      { key: 'Denied', label: 'Denied' },
      { key: 'Private Pay', label: 'Private Pay' },
    ]
  
  const keysAndLabelsApproved = [
    {key: 'Approved',label: 'Approved'},
    {key: 'Approved No RTC', label: 'Approved No RTC'},
    {key: 'Approved LB No RTC',label: 'Approved LB No RTC'},
    {key: 'Approved DB',label: 'Approved DB'},
  ]

  const keysAndLabelsCheckedIn = [
    {key: 'Arrived',label: 'Arrived'},
    {key: 'Pending Arrival',label: 'Pending Arrival'},
    {key: 'MIA',label: 'MIA'},
    {key: 'BD',label: 'BD'},
  ]

  const keysAndLabelsCensus = [
    {key: 'Total Patients',label: 'Total Patients'},
    {key: 'New Admits',label: 'New Admits'},
    {key: 'RTC',label: 'RTC'},
    {key: 'Detox',label: 'Detox'},
    {key: 'PHP',label: 'PHP'},
    {key: 'IOP',label: 'IOP'},
  ]

  const keysAndLabelsAnticipatedDischarge = [
    {key: 'Axis',label: 'Axis'},
    {key: 'Affinity',label: 'Affinity'},
    {key: 'Beachside',label: 'Beachside'},
  ]

  const keysAndLabelsCoordinators = [
    {key: 'Dylan Finch',label: 'Dylan F.'},
    {key: 'Whitney  Meeder',label: 'Whitney M.'},
    {key: 'Cynthia Chacon',label: 'Cynthia C.'},
    {key: 'Branden  Cox ',label: 'Branden C.'},
    {key: 'randi Hamilton',label: 'Randi H.'},
    {key: 'Kelly Mahaffey',label: 'Kelly M.'},
    {key: 'Kevyn Howard',label: 'Kevyn H.'},
    {key: 'MICHAEL CROTHERS', label: 'Michael C.'}
  ]

  const COLORS = [
    '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'
  ];

  const colors = {
    'Pending Approval': "bg-custom-macaroni",
    'Total Approved': "bg-custom-orange",
    'Denied': "bg-custom-red",
    'Private Pay': "bg-custom-yellow",
    'Approved': 'bg-custom-macaroni',
    'Approved No RTC': "bg-custom-orange",
    'Approved LB No RTC': "bg-custom-red",
    'Approved DB': "bg-custom-yellow",
    'Arrived': 'bg-custom-macaroni',
    'Pending Arrival': 'bg-custom-orange',
    'MIA': 'bg-custom-red',
    'BD': 'bg-custom-yellow',
    'Total Patients': 'bg-custom-red',
    'New Admits': 'bg-custom-green',
    'Detox': 'bg-custom-teal',
    'RTC': 'bg-custom-blue',
    'PHP':'bg-custom-orange',
    'IOP': 'bg-custom-violet',
    'Axis': 'bg-custom-blue',
    'Affinity': 'bg-custom-red',
    'Beachside': 'bg-custom-green'
  }

  const coordinatorColors = {
    'Dylan Finch': 'bg-custom-blue',
    'Whitney  Meeder': 'bg-custom-red',
    'Cynthia Chacon': 'bg-custom-teal',
    "Branden  Cox ": 'bg-custom-violet',
    'randi Hamilton': 'bg-custom-lime',
    'Kelly Mahaffey': 'bg-custom-brick',
    'Kevyn Howard': 'bg-custom-orange',
    'MICHAEL CROTHERS': 'bg-custom-pink'
  }

  
  const COLORS2:any = 
    {
      'Dylan Finch': '#0000FF',
      'Whitney  Meeder': '#FF4500',
      'Cynthia Chacon': '#008080',
      "Branden  Cox ": '#7F00FF',
      'randi Hamilton': '#32CD32',
      'Kelly Mahaffey': '#A52A2A',
      'Kevyn Howard': '#fc8003',
      'MICHAEL CROTHERS': '#FF1493'
    }

  const grabCensusData = async () => {
    setCensusLoading(true)
    try {
      const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
      const formatDate = (d: Date) => d.toISOString().split('T')[0]
      const isToday = dashboardRange === 'Today'
      const params = isToday
        ? `days=${days}&forecast=true`
        : `start_date=${formatDate(dashboardStartDate)}&end_date=${formatDate(dashboardEndDate)}&forecast=true`
      const url = `${baseUrl}/analytics/census?${params}`
      console.log('Fetching census from:', url)
      const response = await axios.get(url)
      console.log('Census API response:', response.data.total_census, response.data.total_pending)
      setCensusAnalytics(response.data)

      // Also set legacy censusData for backward compatibility
      const legacyData = (response.data.by_facility || []).map((f: any) => ({
        facility: f.facility,
        'Total Patients': f.total_patients,
        'New Admits': f.other || 0,
        'RTC': f.rtc,
        'Detox': f.detox,
        'PHP': f.php,
        'IOP': f.iop
      }))
      setCensusData(legacyData)
    } catch (error) {
      console.log(error)
      setCensusAnalytics(null)
    } finally {
      setCensusLoading(false)
    }
  }

  const grabCensusClaims = async () => {
    setCensusClaimsLoading(true)
    try {
      const formatDate = (d: Date) => d.toISOString().split('T')[0]
      const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
      const isToday = dashboardRange === 'Today'
      const params = isToday
        ? `days=${days}`
        : `start=${formatDate(dashboardStartDate)}&end=${formatDate(dashboardEndDate)}`
      const response = await axios.get(`${baseUrl}/analytics/census-claims?${params}`)
      setCensusClaims(response.data)
    } catch (error) {
      console.log('Census claims error:', error)
      setCensusClaims(null)
    } finally {
      setCensusClaimsLoading(false)
    }
  }

  const grabCrmDashboard = async () => {
    const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
    setCrmLoading(true)
    setCrmError(null)
    try {
      const formatYYYYMMDD = (d: Date) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${dd}`
      }

      const url =
        dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
          ? `${baseUrl}/intake/crm/dashboard?start=${formatYYYYMMDD(dashboardStartDate)}&end=${formatYYYYMMDD(dashboardEndDate)}`
          : `${baseUrl}/intake/crm/dashboard?days=${days}`

      const response = await axios.get(url)
      setCrmDashboard(response.data)

      // Fetch previous period for comparison
      let prevStart: Date
      let prevEnd: Date
      if (dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday') {
        const diffMs = dashboardEndDate.getTime() - dashboardStartDate.getTime()
        prevEnd = new Date(dashboardStartDate.getTime() - 86400000) // day before start
        prevStart = new Date(prevEnd.getTime() - diffMs)
      } else {
        // For preset ranges (Last 7 days, Last 30 days), calculate previous period
        const today = new Date()
        const currentEnd = today
        const currentStart = new Date(today.getTime() - (days - 1) * 86400000)
        prevEnd = new Date(currentStart.getTime() - 86400000) // day before current start
        prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000)
      }
      const prevUrl = `${baseUrl}/intake/crm/dashboard?start=${formatYYYYMMDD(prevStart)}&end=${formatYYYYMMDD(prevEnd)}`
      try {
        const prevResponse = await axios.get(prevUrl)
        setCrmPreviousPeriod(prevResponse.data)
      } catch {
        setCrmPreviousPeriod(null)
      }
    } catch (e: any) {
      console.log(e)
      setCrmError(e?.response?.data?.error || e?.message || 'Failed to load CRM dashboard')
      setCrmDashboard(null)
      setCrmPreviousPeriod(null)
    } finally {
      setCrmLoading(false)
    }
  }

  const grabCrmLeads = async () => {
    try {
      const response = await axios.get(`${baseUrl}/intake/crm/leads?limit=10&offset=0`)
      setCrmLeads(response?.data?.data || [])
    } catch (e) {
      console.log(e)
      setCrmLeads([])
    }
  }

  const grabBankBalance = async () => {
    setBankBalanceLoading(true)
    try {
      const response = await axios.get(`${baseUrl}/analytics/balances`)
      setBankBalance(response.data.total_balance || 0)
    } catch (e) {
      console.log(e)
    } finally {
      setBankBalanceLoading(false)
    }
  }

  const grabDepositData = async () => {
    const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
    const formatYYYYMMDD = (d: Date) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    }
    const url = dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
      ? `${baseUrl}/analytics/deposits?start=${formatYYYYMMDD(dashboardStartDate)}&end=${formatYYYYMMDD(dashboardEndDate)}&forecast=true`
      : `${baseUrl}/analytics/deposits?days=${days}&forecast=true`

    let prevStart: Date
    let prevEnd: Date
    if (dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday') {
      const diffMs = dashboardEndDate.getTime() - dashboardStartDate.getTime()
      prevEnd = new Date(dashboardStartDate.getTime() - 86400000)
      prevStart = new Date(prevEnd.getTime() - diffMs)
    } else {
      const today = new Date()
      const currentStart = new Date(today.getTime() - (days - 1) * 86400000)
      prevEnd = new Date(currentStart.getTime() - 86400000)
      prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000)
    }
    const prevUrl = `${baseUrl}/analytics/deposits?start=${formatYYYYMMDD(prevStart)}&end=${formatYYYYMMDD(prevEnd)}`

    // Stale-while-revalidate: show cached result immediately, refetch silently.
    const cached = analyticsCache.get(url)
    const cachedPrev = analyticsCache.get(prevUrl)
    if (cached) {
      setDepositData(cached)
      setDepositPreviousPeriod(cachedPrev ?? null)
    } else {
      setDepositLoading(true)
    }

    try {
      const [currentRes, prevRes] = await Promise.all([
        axios.get(url),
        axios.get(prevUrl).catch(() => null)
      ])
      analyticsCache.set(url, currentRes.data)
      if (prevRes) analyticsCache.set(prevUrl, prevRes.data)
      persistAnalyticsCache()
      setDepositData(currentRes.data)
      setDepositPreviousPeriod(prevRes ? prevRes.data : null)
    } catch (e) {
      console.log(e)
      if (!cached) {
        setDepositData(null)
        setDepositPreviousPeriod(null)
      }
    } finally {
      setDepositLoading(false)
    }
  }

  const grabExpenseData = async () => {
    const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
    const formatYYYYMMDD = (d: Date) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    }
    const url = dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
      ? `${baseUrl}/analytics/expenses?start=${formatYYYYMMDD(dashboardStartDate)}&end=${formatYYYYMMDD(dashboardEndDate)}&forecast=true`
      : `${baseUrl}/analytics/expenses?days=${days}&forecast=true`

    const cached = analyticsCache.get(url)
    if (cached) {
      setExpenseData(cached)
    } else {
      setExpenseLoading(true)
    }

    try {
      const response = await axios.get(url)
      analyticsCache.set(url, response.data)
      persistAnalyticsCache()
      setExpenseData(response.data)
    } catch (e) {
      console.log(e)
      if (!cached) setExpenseData(null)
    } finally {
      setExpenseLoading(false)
    }
  }

  const grabRevenueData = async () => {
    setRevenueLoading(true)
    try {
      const response = await axios.get(`${baseUrl}/analytics/revenue-forecast`)
      setRevenueData(response.data)
    } catch (e) {
      console.log(e)
      setRevenueData(null)
    } finally {
      setRevenueLoading(false)
    }
  }

  const fetchLinkToken = async () => {
    try {
      const response = await axios.post(`${baseUrl}/analytics/plaid/create-link-token`)
      setLinkToken(response.data.link_token)
    } catch (e) {
      console.error('Error fetching Plaid link token:', e)
    }
  }

  const handlePlaidSuccess = async (publicToken: string) => {
    try {
      await axios.post(`${baseUrl}/analytics/plaid/exchange-token`, { public_token: publicToken })
      setPlaidLinked(true)
      grabDepositData()
      grabBankBalance()
      grabExpenseData()
    } catch (e) {
      console.error('Error exchanging Plaid token:', e)
    }
  }

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken) => handlePlaidSuccess(publicToken),
  })

  const grabLeadsForDay = async (dayYmd: string) => {
    setSelectedDayLoading(true)
    try {
      const response = await axios.get(
        `${baseUrl}/intake/crm/leads?start=${dayYmd}&end=${dayYmd}&limit=25&offset=0`
      )
      setSelectedDayLeads(response?.data?.data || [])
    } catch (e) {
      console.log(e)
      setSelectedDayLeads([])
    } finally {
      setSelectedDayLoading(false)
    }
  }

  const grabCallData = async () => {
    setCallLoading(true)
    setCallError(null)
    try {
      const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
      const formatYYYYMMDD = (d: Date) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${dd}`
      }
      let url = dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
        ? `${baseUrl}/analytics/calls?start=${formatYYYYMMDD(dashboardStartDate)}&end=${formatYYYYMMDD(dashboardEndDate)}&forecast=true`
        : `${baseUrl}/analytics/calls?days=${days}&forecast=true`
      let response = await axios.get(url)

      // Flag if today has no call data yet
      if (dashboardRange === 'Today' && (!response.data?.timeseries?.length || response.data?.totals?.total_inbound === 0)) {
        setCallYesterdayFallback(true)
      } else {
        setCallYesterdayFallback(false)
      }
      setCallData(response.data)

      // Fetch previous period for comparison
      let prevStart: Date
      let prevEnd: Date
      if (dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday') {
        const diffMs = dashboardEndDate.getTime() - dashboardStartDate.getTime()
        prevEnd = new Date(dashboardStartDate.getTime() - 86400000)
        prevStart = new Date(prevEnd.getTime() - diffMs)
      } else {
        const today = new Date()
        const currentStart = new Date(today.getTime() - (days - 1) * 86400000)
        prevEnd = new Date(currentStart.getTime() - 86400000)
        prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000)
      }
      const prevUrl = `${baseUrl}/analytics/calls?start=${formatYYYYMMDD(prevStart)}&end=${formatYYYYMMDD(prevEnd)}`
      try {
        const prevResponse = await axios.get(prevUrl)
        setCallPreviousPeriod(prevResponse.data)
      } catch {
        setCallPreviousPeriod(null)
      }
    } catch (e: any) {
      console.log(e)
      setCallError(e?.response?.data?.error || e?.message || 'Failed to load call data')
      setCallData(null)
      setCallPreviousPeriod(null)
    } finally {
      setCallLoading(false)
    }
  }

  const dashboardOptions = ['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'Custom']

  const dashboardRangeLabel = useMemo(() => {
    if (dashboardRange !== 'Custom') return dashboardRange
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    return `${fmt(dashboardStartDate)} - ${fmt(dashboardEndDate)}`
  }, [dashboardRange, dashboardStartDate, dashboardEndDate])

  const [csvMenuOpen, setCsvMenuOpen] = useState<boolean>(false)
  const csvBtnRef = useRef<HTMLButtonElement | null>(null)
  const [csvMenuPos, setCsvMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 })

  const downloadCsv = (filename: string, rows: Record<string, any>[]) => {
    if (!rows.length) {
      alert('No data available to download for this report.')
      return
    }
    const headers = Object.keys(rows[0])
    const escape = (v: any) => {
      if (v === null || v === undefined) return ''
      const s = String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => escape(r[h])).join(','))
    ].join('\n')
    const stamp = dashboardRangeLabel.replace(/[^\w]+/g, '_')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}_${stamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const fetchGeoStats = async () => {
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const res = await axios.get(`${baseUrl}/gps/stats`, {
      params: { from: fmt(dashboardStartDate), to: fmt(dashboardEndDate) },
    })
    return res.data
  }

  const handleDownloadReport = async (report: string) => {
    setCsvMenuOpen(false)
    switch (report) {
      case 'Cities':
        return downloadCsv('cities', ((await fetchGeoStats()).top_cities || []).map((c: any) => ({
          city: c.city,
          state: c.state,
          live_sites: c.live_count,
          unique_callers: c.calls,
        })))
      case 'Sites':
        return downloadCsv('sites', ((await fetchGeoStats()).top_facilities || []).map((f: any) => ({
          name: f.name,
          city: f.city,
          state: f.state,
          phone: f.phone,
          ring_group: f.ring_group,
          unique_callers: f.calls,
        })))
      case 'Numbers':
        return downloadCsv('numbers', ((await fetchGeoStats()).top_phones || []).map((p: any) => ({
          phone: p.phone,
          site: p.name,
          city: p.city,
          state: p.state,
          ring_group: p.ring_group,
          unique_callers: p.calls,
        })))
      case 'Ring Groups':
        if (mainTab === 'Marketing') {
          return downloadCsv('ring_groups', ((await fetchGeoStats()).top_ring_groups || []).map((g: any) => ({
            ring_group: g.ring_group,
            live_sites: g.live_count,
            unique_callers: g.calls,
          })))
        }
        return downloadCsv('ring_groups', (callMetrics.byRingGroup || []).map((g: any) => ({
          ring_group: g.ring_group,
          inbound: g.total_inbound || 0,
          leads: g.total_leads || 0,
          booked: g.total_booked || 0,
          arrived: g.total_arrived || 0,
          talk_time_mins: g.total_talk_time_mins || 0,
          avg_talk_mins: g.avg_talk_time_mins || 0,
        })))
      case 'Coordinators':
        return downloadCsv('coordinators', (callMetrics.byCoordinator || []).map((c: any) => ({
          coordinator: c.coordinator_name,
          answered: c.total_answered || 0,
          outbound: c.total_outbound || 0,
          talk_time_mins: c.total_talk_time_mins || 0,
          avg_talk_mins: c.avg_talk_time_mins || 0,
        })))
      case 'Facilities':
        return downloadCsv('facilities', (crmDashboard?.by_facility || []).map((f: any) => ({ ...f })))
      case 'Leads (Daily)':
        return downloadCsv('leads_daily', (crmDashboard?.timeseries || []).map((t: any) => ({ ...t })))
      case 'Census by Facility':
        return downloadCsv('census_by_facility', (censusAnalytics?.by_facility || []).map((f: any) => ({
          facility: f.facility,
          total_patients: f.total_patients,
          rtc: f.rtc || 0,
          php: f.php || 0,
          detox: f.detox || 0,
          iop: f.iop || 0,
        })))
      case 'Discharges (All)':
        return downloadCsv('discharges_all', (censusAnalytics?.discharges || []).map((d: any) => ({
          'DC Reason': d.dc_reason,
          'Client Name': d.patient_name,
          'Facility': d.facility || '',
          'Admission Date': d.admission_date || '',
          'Discharge Date': d.discharge_date || '',
          'Days Stayed': d.days_stayed,
          'Insurance': d.insurance,
          'Level of Care': d.level_of_care,
        })))
      case 'ACA / Admin DCs':
        return downloadCsv('aca_admin_dcs', (censusAnalytics?.discharges || [])
          .filter((d: any) => d.dc_reason === 'Left Voluntarily/ACA' || d.dc_reason === 'Administrative Discharged')
          .map((d: any) => ({
            'DC Reason': d.dc_reason,
            'Client Name': d.patient_name,
            'Facility': d.facility || '',
            'Admission Date': d.admission_date || '',
            'Discharge Date': d.discharge_date || '',
            'Days Stayed': d.days_stayed,
            'Insurance': d.insurance,
            'Level of Care': d.level_of_care,
          })))
      case 'Deposits by Account':
        return downloadCsv('deposits_by_account', (depositData?.by_account || []).map((a: any) => ({ ...a })))
      case 'ERA by Payer':
        return downloadCsv('era_by_payer', (depositData?.era_by_payer || []).map((p: any) => ({ ...p })))
      case 'Expenses by Category':
        return downloadCsv('expenses_by_category', (expenseData?.by_category || []).map((c: any) => ({ ...c })))
      case 'Top Merchants':
        return downloadCsv('top_merchants', (expenseData?.top_merchants || []).map((m: any) => ({
          merchant: m.merchant,
          transaction_count: m.transaction_count,
          total_amount: m.total_amount,
          accounts: Array.isArray(m.accounts) ? m.accounts.join('; ') : m.accounts,
        })))
    }
  }

  const csvReportOptions = mainTab === 'Marketing'
    ? ['Cities', 'Sites', 'Numbers', 'Ring Groups']
    : [
        'Ring Groups',
        'Coordinators',
        'Facilities',
        'Leads (Daily)',
        'Census by Facility',
        'Discharges (All)',
        'ACA / Admin DCs',
        'Deposits by Account',
        'ERA by Payer',
        'Expenses by Category',
        'Top Merchants',
      ]

  const [sortState, setSortState] = useState<Record<string, { key: string; dir: 'asc' | 'desc' }>>({})
  const toggleSort = (table: string, key: string) => {
    setSortState(s => {
      const cur = s[table]
      if (cur?.key === key) return { ...s, [table]: { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } }
      return { ...s, [table]: { key, dir: 'desc' } }
    })
  }
  const sortRows = <T extends Record<string, any>>(table: string, rows: T[], getter?: (r: T, key: string) => any): T[] => {
    const s = sortState[table]
    if (!s) return rows
    const arr = [...rows]
    arr.sort((a, b) => {
      const va = getter ? getter(a, s.key) : a[s.key]
      const vb = getter ? getter(b, s.key) : b[s.key]
      if (va === vb) return 0
      if (va === null || va === undefined) return 1
      if (vb === null || vb === undefined) return -1
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true })
      return s.dir === 'asc' ? cmp : -cmp
    })
    return arr
  }
  const sortTh = (table: string, skey: string, label: string) => {
    const s = sortState[table]
    const active = s?.key === skey
    const arrow = !active ? '↕' : s.dir === 'asc' ? '↑' : '↓'
    return (
      <th
        key={skey}
        onClick={() => toggleSort(table, skey)}
        className="py-2 pr-4 font-medium cursor-pointer select-none hover:text-white"
      >
        {label} <span className={`text-xs ${active ? 'text-accent-teal' : 'text-gray-500'}`}>{arrow}</span>
      </th>
    )
  }

  const onDashboardStartChange = (d: Date) => {
    setDashboardStartDate(d)
    if (d > dashboardEndDate) setDashboardEndDate(d)
  }

  const onDashboardEndChange = (d: Date) => {
    if (d < dashboardStartDate) {
      setDashboardStartDate(d)
      setDashboardEndDate(d)
      return
    }
    setDashboardEndDate(d)
  }

  // Update dates when range selection changes
  useEffect(() => {
    const today = new Date()
    if (dashboardRange === 'Today') {
      setDashboardStartDate(today)
      setDashboardEndDate(today)
    } else if (dashboardRange === 'Yesterday') {
      const yesterday = new Date(today)
      yesterday.setDate(today.getDate() - 1)
      setDashboardStartDate(yesterday)
      setDashboardEndDate(yesterday)
    } else if (dashboardRange === 'Last 7 days') {
      const start = new Date(today)
      start.setDate(today.getDate() - 6)
      setDashboardStartDate(start)
      setDashboardEndDate(today)
    } else if (dashboardRange === 'Last 30 days') {
      const start = new Date(today)
      start.setDate(today.getDate() - 29)
      setDashboardStartDate(start)
      setDashboardEndDate(today)
    }
    // For 'Custom', don't change the dates - let user pick
  }, [dashboardRange])

  // Prepare timeseries data for charts with forecast overlay
  const prepareForecastData = (timeseries: any[], barKeys: string[], dateKey = 'date') => {
    if (!timeseries?.length) return { data: [], lastHistoricalDate: null }
    const historical = timeseries.filter((d: any) => !d.isForecast)
    const lastHistoricalDate = historical.length > 0 ? historical[historical.length - 1][dateKey] : null
    const data = timeseries.map((d: any) => {
      const out: any = { ...d }
      for (const key of barKeys) {
        if (d.isForecast) {
          out[`forecast_${key}`] = d[key]
          out[key] = null
        } else {
          out[`forecast_${key}`] = null
        }
      }
      return out
    })
    // Bridge: set last historical point's forecast keys to its values so area connects
    if (lastHistoricalDate) {
      const bridgeIdx = data.findIndex((d: any) => d[dateKey] === lastHistoricalDate)
      if (bridgeIdx >= 0) {
        for (const key of barKeys) {
          data[bridgeIdx][`forecast_${key}`] = data[bridgeIdx][key]
        }
      }
    }
    return { data, lastHistoricalDate }
  }

  // Custom tooltip for forecast charts
  const ForecastTooltip = ({ active, payload, label, formatter }: any) => {
    if (!active || !payload?.length) return null
    const isForecast = payload[0]?.payload?.isForecast
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl">
        <div className="text-white text-sm font-medium">
          {new Date(label).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
        </div>
        {isForecast && <div className="text-xs text-yellow-400 mb-1 font-medium">Projected</div>}
        {payload.filter((p: any) => p.value != null).map((p: any, i: number) => (
          <div key={i} className="text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-gray-300">{p.name}:</span>
            <span className="text-white font-medium">{formatter ? formatter(p.value, p.name) : p.value}</span>
          </div>
        ))}
      </div>
    )
  }

  const dashboardSeries = useMemo(() => {
    const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
    const ts = Array.isArray(crmDashboard?.timeseries) ? crmDashboard.timeseries : []
    const list = ts.map((d: any) => {
      // Parse date string as local timezone (not UTC) to avoid day shift
      const [year, month, day] = (d?.date || '').split('-').map(Number)
      const dt = new Date(year, month - 1, day)
      const label =
        dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
          ? dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, month: 'short', day: 'numeric' })
          : days === 7
          ? dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, weekday: 'short', month: 'numeric', day: 'numeric' })
          : dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, month: 'short', day: 'numeric' })
      return {
        ...d,
        key: d?.date,
        label,
        pendingApproval: d?.checked_in_pending ?? 0,
        totalApproved: d?.approved ?? 0,
      }
    })
    const totals = {
      total: crmDashboard?.kpis?.total ?? 0,
      pendingApproval: crmDashboard?.kpis?.new ?? 0,
      booked: crmDashboard?.kpis?.booked ?? 0,
      totalApproved: crmDashboard?.kpis?.approved ?? 0,
      arrived: crmDashboard?.kpis?.arrived ?? 0,
      lost: crmDashboard?.kpis?.lost ?? 0,
      denied: crmDashboard?.kpis?.denied ?? 0,
    }
    return { list, totals }
  }, [crmDashboard, dashboardRange, currentTimeZone])

  // Call metrics computed from callData
  const callMetrics = useMemo(() => {
    if (!callData) {
      return {
        timeseries: [],
        totals: {
          totalInbound: 0,
          totalMissed: 0,
          totalOutbound: 0,
          totalTalkTimeMins: 0,
          avgTalkTimeMins: 0
        },
        byCoordinator: [],
        byRingGroup: []
      }
    }

    const days = dashboardRange === 'Last 30 days' ? 30 : dashboardRange === 'Yesterday' ? 1 : 7
    const ts = Array.isArray(callData?.timeseries) ? callData.timeseries : []
    const timeseries = ts.map((d: any) => {
      // Parse date string as local timezone (not UTC) to avoid day shift
      const [year, month, day] = (d?.report_date || '').split('-').map(Number)
      const dt = new Date(year, month - 1, day)
      const label =
        dashboardRange === 'Custom' || dashboardRange === 'Today' || dashboardRange === 'Yesterday'
          ? dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, month: 'short', day: 'numeric' })
          : days === 7
          ? dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, weekday: 'short', month: 'numeric', day: 'numeric' })
          : dt.toLocaleDateString('en-US', { timeZone: currentTimeZone, month: 'short', day: 'numeric' })
      return {
        ...d,
        label
      }
    })

    const totals = {
      totalInbound: callData?.totals?.total_inbound ?? 0,
      totalMissed: callData?.totals?.total_missed ?? 0,
      totalOutbound: callData?.totals?.total_outbound ?? 0,
      totalTalkTimeMins: callData?.totals?.total_talk_time_mins ?? 0,
      avgTalkTimeMins: callData?.totals?.avg_talk_time_mins ?? 0
    }

    const byCoordinator = Array.isArray(callData?.by_coordinator) ? callData.by_coordinator : []
    const byRingGroup = Array.isArray(callData?.by_ring_group) ? callData.by_ring_group : []

    return { timeseries, totals, byCoordinator, byRingGroup }
  }, [callData, dashboardRange, currentTimeZone])

  const processIntakeInformation = (intakeRecords: IntakeProps[] | null) => {
    let analytics: any = {};

    // const startDate = new Date();
    // switch (timespan) {
    //   case '1 Week':
    //     startDate.setDate(startDate.getDate() - 7);
    //     break;
    //   case '1 Month':
    //     startDate.setMonth(startDate.getMonth() - 1);
    //     break;
    //   case '3 Months':
    //     startDate.setMonth(startDate.getMonth() - 3);
    //     break;
    //   case '6 Months':
    //     startDate.setMonth(startDate.getMonth() - 6);
    //     break;
    //   case '1 Year':
    //     startDate.setFullYear(startDate.getFullYear() - 1);
    //     break;
    //   default:
    //     break;
    // }

    function convertDobDateToMMDDYYYY(dateString: string) {
      const date = new Date(dateString);
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0'); 
      const dd = String(date.getUTCDate() + 1).padStart(2, '0'); 
      return `${mm}/${dd}`;
    }

    intakeRecords?.forEach(item => {
      const { coordinator, date, booked, checked_in } = item;
      const currentDate = new Date(date);
      if (statusStartDate <= currentDate && statusEndDate >= currentDate) {
        if (!analytics[convertDobDateToMMDDYYYY(date)]) {
          analytics[convertDobDateToMMDDYYYY(date)] = {
            clients: 0,
            date: convertDobDateToMMDDYYYY(date),
            Arrived: 0,
            MIA: 0,
            BD: 0,
            'Pending Arrival': 0,
            Approved: 0,
            Denied: 0,
            'Private Pay': 0,
            'Approved No RTC': 0,
            'Approved LB No RTC': 0,
            'Pending Approval': 0,
            'Approved DB': 0,
            'Total Approved': 0,
            waiting_arrival: 0,
            'Booked': 0,
            'Incoming Clients': 0,
            'Total Calls': 0,
            arrived_approved_percent: 0,
            approved_total_percent: 0,
            arrived_total_percent: 0,
          };
        }

        analytics[convertDobDateToMMDDYYYY(date)]['Total Calls']++;

        if (booked === 'Arrived') analytics[convertDobDateToMMDDYYYY(date)].Arrived++;
        if (booked === 'MIA') analytics[convertDobDateToMMDDYYYY(date)].MIA++;
        if (booked === 'BD') analytics[convertDobDateToMMDDYYYY(date)].BD++;
        if (booked === 'Pending') analytics[convertDobDateToMMDDYYYY(date)]['Pending Arrival']++;
        
        if (checked_in === 'Pending') analytics[convertDobDateToMMDDYYYY(date)]['Pending Approval']++;
        if (checked_in === 'Approved') analytics[convertDobDateToMMDDYYYY(date)].Approved++;
        if (checked_in === 'Denied') analytics[convertDobDateToMMDDYYYY(date)].Denied++;
        if (checked_in === 'Private Pay') analytics[convertDobDateToMMDDYYYY(date)]['Private Pay']++;
        if (checked_in === 'Approved DB') analytics[convertDobDateToMMDDYYYY(date)]['Approved DB']++;
        if (checked_in === 'Approved No RTC') analytics[convertDobDateToMMDDYYYY(date)]['Approved No RTC']++;
        if (checked_in === 'Approved LB No RTC') analytics[convertDobDateToMMDDYYYY(date)]['Approved LB No RTC']++;
  
        if (checked_in === 'Approved LB No RTC' || 
            checked_in === 'Approved DB' || 
            checked_in === 'Approved No RTC' || 
            checked_in === 'Approved' ) analytics[convertDobDateToMMDDYYYY(date)]['Total Approved']++;

      }

    });
    let newData: any = []
    Object.values(analytics).map((record) => {
      newData.push(record)
    })
    setAnalyticsData(newData.reverse())
  }

  const grabIntakeConversionData = async () => {
    const body = {
      "tz": currentTimeZone
    }

    let config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: `${baseUrl}/intake/conversion`,
      headers: { },
      data: body
    };

    try {
      const response = await axios.request(config);
      console.log(response.data, "this is conversion chart data");
      setConversionData(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  // const coordinatorConversionData = (conversionData: any) => {
  //   const processedData: any = {
  //     Booked: {},
  //     Approved: {}
  //   };
  
  //   conversionData.forEach((item: any) => {
  //     const coordinatorId = item.coordinator;
  
  //     const user = allUsers?.find(user => user.userid === coordinatorId);
  //     if (!user || user.name === 'Unknown User') {
  //       return; // Exclude coordinators without a name or with 'Unknown User'
  //     }
  
  //     const coordinatorName = user.name;
  
  //     if (item.status === 'Booked') {
  //       if (!processedData.Booked[coordinatorName]) {
  //         processedData.Booked[coordinatorName] = 0;
  //       }
  //       processedData.Booked[coordinatorName] += 1;
  //     }
  
  //     if (item.status === 'Approved') {
  //       if (!processedData.Approved[coordinatorName]) {
  //         processedData.Approved[coordinatorName] = 0;
  //       }
  //       processedData.Approved[coordinatorName] += 1;
  //     }
  //   });
  
  //   const finalResponse: any = [];
  //   Object.keys(processedData).forEach(status => {
  //     Object.keys(processedData[status]).forEach(coordinator => {
  //       finalResponse.push({
  //         name: `${status} - ${coordinator}`,
  //         value: processedData[status][coordinator]
  //       });
  //     });
  //   });
  //   setPieData(finalResponse);
  // };

  const filterCoordinatorsData = (conversionData: any) => {
    const processedData: any = {};

    conversionData.forEach((item: any) => {
      const coordinatorId = item.coordinator;
      const user = allUsers?.find(user => user.userid === coordinatorId);
      if (!user || user.name === 'Unknown User') {
        return; // Exclude coordinators without a name or with 'Unknown User'
      }

      const coordinatorName = user.name;
      if (!processedData[coordinatorName]) {
        processedData[coordinatorName] = { booked: 0, arrived: 0, total: 0 };
      }

      // Count all leads for total
      processedData[coordinatorName].total += 1;

      if (item.status === 'Booked') {
        processedData[coordinatorName].booked += 1;
      } else if (item.status === 'Arrived') {
        processedData[coordinatorName].arrived += 1;
      }
    });

    return Object.keys(processedData).map(coordinator => {
      const { booked, arrived, total } = processedData[coordinator];
      const converted = booked + arrived;
      const conversionRate = total > 0 ? (converted / total) * 100 : 0;
      return {
        name: coordinator,
        value: parseFloat(conversionRate.toFixed(2))
      };
    });
    };

  const filterDataByTimeFrame = (timeFrame: any) => {
    const currentDate = new Date();
    let startDate: Date;

    switch (timeFrame) {
      // case ''
      case '1 Week':
        startDate = new Date(currentDate.setDate(currentDate.getDate() - 7));
        break;
      case '1 Month':
        startDate = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
        break;
      case '3 Months':
        startDate = new Date(currentDate.setMonth(currentDate.getMonth() - 3));
        break;
      case '6 Months':
        startDate = new Date(currentDate.setMonth(currentDate.getMonth() - 6));
        break;
      default:
        startDate = new Date(0); // Include all dates if no valid time frame is provided
        break;
    }
    return conversionData.filter((item: any) => new Date(item.date) >= startDate);
  };

  const filterDataCustom = (startDate: Date, endDate: Date) => {

    return conversionData.filter((item: any) => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate<= endDate
    })
  }

  const aggregateData = (filteredData: any) => {
    const aggregatedData: any = {
      Booked: { total: 0, coordinators: {} },
      Arrived: { total: 0, coordinators: {} },
      Denied: { total: 0, coordinators: {} },
      Pending: { total: 0, coordinators: {} }
    };

    filteredData.forEach((item: any) => {
      const coordinator = allUsers?.find(user => user.userid === item.coordinator)?.name || 'Unknown';

      if (item.status === 'Booked') {
        aggregatedData.Booked.total += 1;
        aggregatedData.Booked.coordinators[coordinator] = (aggregatedData.Booked.coordinators[coordinator] || 0) + 1;
      } else if (item.status === 'Arrived') {
        aggregatedData.Arrived.total += 1;
        aggregatedData.Arrived.coordinators[coordinator] = (aggregatedData.Arrived.coordinators[coordinator] || 0) + 1;
      } else if (item.status === 'Denied') {
        aggregatedData.Denied.total += 1;
        aggregatedData.Denied.coordinators[coordinator] = (aggregatedData.Denied.coordinators[coordinator] || 0) + 1;
      } else if (item.status === 'Pending') {
        aggregatedData.Pending.total += 1;
        aggregatedData.Pending.coordinators[coordinator] = (aggregatedData.Pending.coordinators[coordinator] || 0) + 1;
      }
    });

    return Object.entries(aggregatedData).map(([name, data]) => ({
      name,
      value: (data as any).total,
      coordinators: (data as any).coordinators
    }));
  };

  const filterConversionByTimeCustom = (startDate:Date, endDate:Date) => {
    const filteredData = filterDataCustom(startDate, endDate);
    const aggregatedData = aggregateData(filteredData);
    setOverallPieData(aggregatedData);
    setPieBookedData(filterCoordinatorsData(filteredData));
  };
  
  const renderOverallCustomizedLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value
  }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <text
          x={x}
          y={y}
          fill="grey"
          textAnchor={x > cx ? 'start' : 'end'}
          color='grey'
          dominantBaseline="central"
        >
          {`${name} (${value})`}
        </text>
        <text
          x={x}
          y={y}
          dy={18}
          fill="grey"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
        >
          {`${(percent * 100).toFixed(2)}%`}
        </text>
      </g>
    );
  };


  const renderPieCustomizedLabel = ({
    cx, cy, midAngle, outerRadius, percent, name, value, index
  }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + (radius + 10) * Math.cos(-midAngle * RADIAN);
    const y = cy + (radius + 20) * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <text
          x={x}
          y={y}
          fill='grey'
          textAnchor={x > cx ? 'start' : 'end'}
          fontSizeAdjust={8}
          dominantBaseline="central"
        >
          {`${name} (${value})`}
        </text>
      </g>
    );
  };

  const customTooltipBooked = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const total = pieBookedData.reduce((sum: any, entry: any) => sum + entry.value, 0)
      return (
        <div className="custom-tooltip" style={{ backgroundColor: "#fff", padding: "10px", border: "1px solid #ccc" }}>
          {payload.map((entry: any, index: number) => (
            <div key={`item-${index}`}>
              <span>{entry.name}: </span>
              <span>{`${entry.value} (${((entry.value / total) * 100).toFixed(2)}%)`}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  }

  const customTooltipConversionRate = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: "#fff", padding: "10px", border: "1px solid #ccc" }}>
          {payload.map((entry: any, index: number) => (
            <div key={`item-${index}`}>
              <span>{entry.name}: </span>
              <span>{`${entry.value}%`}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  }

  const customTooltipOverall = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{ backgroundColor: "#fff", padding: "10px", border: "1px solid #ccc" }}>
          <p className="label">{`${data.name}: ${data.value}`}</p>
          <ul style={{ padding: 0, listStyle: 'none' }}>
            {Object.entries(data.coordinators).map(([coordinator, value]) => (
              <li key={coordinator}>{`${coordinator}: ${value}`}</li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };


  const prevRangeRef = useRef<string>(dashboardRange)

  useEffect(() => {
    // When switching TO Custom, don't fetch — wait for user to pick dates
    const switchedToCustom = dashboardRange === 'Custom' && prevRangeRef.current !== 'Custom'
    prevRangeRef.current = dashboardRange
    if (switchedToCustom) return

    if (mainTab === 'Calls & Intake') {
      grabCrmDashboard()
      grabCrmLeads()
      grabCallData()
      grabCensusData()
    } else if (mainTab === 'Deposits') {
      grabDepositData()
      grabExpenseData()
      fetchLinkToken()
    } else if (mainTab === 'Expenses') {
      grabExpenseData()
      fetchLinkToken()
    } else if (mainTab === 'Census') {
      grabCensusData()
      grabCensusClaims()
    } else if (mainTab === 'Revenue Forecast') {
      grabRevenueData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardStartDate, dashboardEndDate, mainTab])

  // Fetch bank balance once when Deposits tab opens (not on range changes to avoid Plaid rate limits)
  useEffect(() => {
    if (mainTab === 'Deposits') {
      grabBankBalance()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab])
  
  

  return (
    <LayoutComponent
      header={null} // Render your custom header component here
      content={
        <div className="w-full h-full px-4">
          <div className="h-full overflow-scroll rounded-lg mt-2">
            {/* Redesigned dashboard view (KPI cards + main chart + heatmap + tabbed breakdown) */}
            <div className="mb-6">
              <div className="mb-5 bg-dark-card/90 backdrop-blur border border-dark-border rounded-2xl px-5 py-4 shadow-sm">
                {crmError ? (
                  <div className="mb-3 text-sm text-red-600">{crmError}</div>
                ) : null}

                {/* Main Dashboard Tabs + Range */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {(['Calls & Intake', 'Marketing', 'Deposits', 'Expenses', 'Census', 'Revenue Forecast'] as const)
                      .filter((t) => {
                        // Intake managers and directors only see Calls & Intake
                        if (currentProfile.department === 'intake' && (currentProfile.privileges === 'manager' || currentProfile.privileges === 'director')) {
                          return t === 'Calls & Intake'
                        }
                        return true
                      })
                      .map((t) => (
                      <button
                        key={t}
                        onClick={() => setMainTab(t)}
                        className={
                          mainTab === t
                            ? 'px-4 py-2 rounded-lg text-sm font-medium bg-accent-teal text-white'
                            : 'px-4 py-2 rounded-lg text-sm font-medium bg-dark-card-hover text-gray-300 border border-dark-border hover:text-white'
                        }
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-400">Range</div>
                      <div className="w-52">
                        <SelectOptionComponent options={dashboardOptions} value={dashboardRange} onChange={setDashboardRange} />
                      </div>
                    </div>

                    {dashboardRange === 'Custom' ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-gray-400">Start</div>
                          <CalendarSelectComponent selectedDate={dashboardStartDate} handleDateChange={onDashboardStartChange} />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-gray-400">End</div>
                          <CalendarSelectComponent selectedDate={dashboardEndDate} handleDateChange={onDashboardEndChange} />
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">{dashboardRangeLabel}</div>
                    )}

                    <div className="sm:ml-auto">
                      <button
                        ref={csvBtnRef}
                        type="button"
                        onClick={() => {
                          if (!csvMenuOpen && csvBtnRef.current) {
                            const r = csvBtnRef.current.getBoundingClientRect()
                            setCsvMenuPos({ top: r.bottom + 8, right: window.innerWidth - r.right })
                          }
                          setCsvMenuOpen(v => !v)
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-accent-teal/20 text-accent-teal hover:bg-accent-teal/30 border border-accent-teal/30 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Report
                      </button>
                      {csvMenuOpen && ReactDOM.createPortal(
                        <>
                          <div className="fixed inset-0 z-[9998]" onClick={() => setCsvMenuOpen(false)} />
                          <div
                            className="fixed w-60 rounded-lg bg-dark-card border border-dark-border shadow-xl z-[9999] py-1"
                            style={{ top: csvMenuPos.top, right: csvMenuPos.right }}
                          >
                            {csvReportOptions.map(opt => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => handleDownloadReport(opt)}
                                className="w-full text-left px-3 py-2 text-xs text-white hover:bg-dark-card-hover transition-colors"
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Calls & Intake Tab Content */}
              {mainTab === 'Calls & Intake' && (
              <>
              {(crmLoading || callLoading) && (!crmDashboard || !callData) ? (
                // Initial-load skeleton: full Calls & Intake layout in shimmer.
                // We only show this when at least one data source hasn't ever
                // populated. Once both have data, subsequent refreshes don't
                // re-show the skeleton (avoids the 0 → real-data flicker).
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="rounded-xl border border-dark-border bg-dark-card p-4 flex flex-col gap-3">
                        <SkeletonBlock className="h-3 w-20" />
                        <SkeletonBlock className="h-7 w-28" />
                        <SkeletonBlock className="h-3 w-16" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rounded-xl border border-dark-border bg-dark-card p-4 flex flex-col gap-3">
                        <SkeletonBlock className="h-3 w-24" />
                        <SkeletonBlock className="h-7 w-32" />
                        <SkeletonBlock className="h-3 w-16" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-xl p-6 flex flex-col gap-4">
                      <SkeletonBlock className="h-5 w-40" />
                      <SkeletonBlock className="h-[260px] w-full" />
                    </div>
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6 flex flex-col gap-4">
                      <SkeletonBlock className="h-5 w-32" />
                      <SkeletonBlock className="h-[260px] w-full" />
                    </div>
                  </div>
                </>
              ) : (
              <>
              {/* Intake KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                <StatCard
                  label="Total Leads"
                  value={dashboardSeries.totals.total}
                  sublabel={dashboardRangeLabel}
                  change={crmPreviousPeriod?.kpis?.total !== undefined
                    ? dashboardSeries.totals.total - crmPreviousPeriod.kpis.total
                    : undefined}
                  changeLabel={crmPreviousPeriod?.kpis?.total !== undefined ? 'vs previous period' : undefined}
                />
                <StatCard
                  label="Approved"
                  value={dashboardSeries.totals.totalApproved}
                  sublabel={dashboardRangeLabel}
                  change={crmPreviousPeriod?.kpis?.approved !== undefined
                    ? dashboardSeries.totals.totalApproved - crmPreviousPeriod.kpis.approved
                    : undefined}
                  changeLabel={crmPreviousPeriod?.kpis?.approved !== undefined ? 'vs previous period' : undefined}
                />
                <StatCard
                  label="Arrived"
                  value={dashboardSeries.totals.arrived}
                  sublabel={dashboardRangeLabel}
                  change={crmPreviousPeriod?.kpis?.arrived !== undefined
                    ? dashboardSeries.totals.arrived - crmPreviousPeriod.kpis.arrived
                    : undefined}
                  changeLabel={crmPreviousPeriod?.kpis?.arrived !== undefined ? 'vs previous period' : undefined}
                />
                <StatCard
                  label="Conversion Rate"
                  value={(() => {
                    const converted = dashboardSeries.totals.booked + dashboardSeries.totals.arrived
                    const eligible = dashboardSeries.totals.total - dashboardSeries.totals.denied
                    return eligible > 0 ? `${Math.round((converted / eligible) * 100)}%` : '0%'
                  })()}
                  sublabel="Converted / Eligible Leads"
                  change={(() => {
                    const converted = dashboardSeries.totals.booked + dashboardSeries.totals.arrived
                    const eligible = dashboardSeries.totals.total - dashboardSeries.totals.denied
                    const currentRate = eligible > 0 ? Math.round((converted / eligible) * 100) : 0
                    const prevBooked = crmPreviousPeriod?.kpis?.booked ?? 0
                    const prevArrived = crmPreviousPeriod?.kpis?.arrived ?? 0
                    const prevTotal = crmPreviousPeriod?.kpis?.total ?? 0
                    const prevDenied = crmPreviousPeriod?.kpis?.denied ?? 0
                    const prevConverted = prevBooked + prevArrived
                    const prevEligible = prevTotal - prevDenied
                    const prevRate = prevEligible > 0 ? Math.round((prevConverted / prevEligible) * 100) : 0
                    return crmPreviousPeriod?.kpis?.total !== undefined ? currentRate - prevRate : undefined
                  })()}
                  changeLabel={crmPreviousPeriod?.kpis?.total !== undefined ? 'pp vs previous' : undefined}
                />
                <StatCard
                  label="Total Calls"
                  value={callLoading ? '...' : (callMetrics.totals.totalInbound + callMetrics.totals.totalOutbound).toLocaleString()}
                  sublabel={dashboardRangeLabel}
                  change={!callLoading && callPreviousPeriod?.totals
                    ? (callMetrics.totals.totalInbound + callMetrics.totals.totalOutbound) -
                      ((callPreviousPeriod.totals.total_inbound ?? 0) + (callPreviousPeriod.totals.total_outbound ?? 0))
                    : undefined}
                  changeLabel={callPreviousPeriod?.totals ? 'vs previous period' : undefined}
                  notice={callYesterdayFallback ? "Today's data available by end of day" : undefined}
                />
              </div>

              {/* Call Volume KPIs */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  label="Inbound Answered"
                  value={callLoading ? '...' : callMetrics.totals.totalInbound.toLocaleString()}
                  sublabel={dashboardRangeLabel}
                  change={!callLoading && callPreviousPeriod?.totals?.total_inbound !== undefined
                    ? callMetrics.totals.totalInbound - callPreviousPeriod.totals.total_inbound
                    : undefined}
                  changeLabel={callPreviousPeriod?.totals?.total_inbound !== undefined ? 'vs previous period' : undefined}
                  notice={callYesterdayFallback ? "Today's data available by end of day" : undefined}
                />
                <StatCard
                  label="Outbound Calls"
                  value={callLoading ? '...' : callMetrics.totals.totalOutbound.toLocaleString()}
                  sublabel={dashboardRangeLabel}
                  change={!callLoading && callPreviousPeriod?.totals?.total_outbound !== undefined
                    ? callMetrics.totals.totalOutbound - callPreviousPeriod.totals.total_outbound
                    : undefined}
                  changeLabel={callPreviousPeriod?.totals?.total_outbound !== undefined ? 'vs previous period' : undefined}
                  notice={callYesterdayFallback ? "Today's data available by end of day" : undefined}
                />
                <StatCard
                  label="Avg Talk Time"
                  value={callLoading ? '...' : `${callMetrics.totals.avgTalkTimeMins.toFixed(1)} min`}
                  sublabel="Per call"
                  change={!callLoading && callPreviousPeriod?.totals?.avg_talk_time_mins !== undefined
                    ? Math.round((callMetrics.totals.avgTalkTimeMins - callPreviousPeriod.totals.avg_talk_time_mins) * 10) / 10
                    : undefined}
                  changeLabel={callPreviousPeriod?.totals?.avg_talk_time_mins !== undefined ? 'min vs previous' : undefined}
                  notice={callYesterdayFallback ? "Today's data available by end of day" : undefined}
                />
              </div>


              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-white font-semibold">Traffic overview</div>
                    <div className="text-xs text-gray-400">{dashboardRangeLabel}</div>
                  </div>
                  <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dashboardSeries.list}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.4)" />
                        <XAxis dataKey="label" tick={{ fill: 'grey' }} />
                        <YAxis tick={{ fill: 'grey' }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="total" name="Total leads" stroke="#6366f1" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="approved" name="Approved" stroke="#10b981" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-white font-semibold">Conversion Rate</div>
                      <div className="text-xs text-gray-400">Converted / Eligible (excl. Denied)</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-[10px]">
                        <div className="w-3 h-3 rounded-sm bg-red-500/80"></div>
                        <span className="text-gray-400">Low</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px]">
                        <div className="w-3 h-3 rounded-sm bg-yellow-500/80"></div>
                        <span className="text-gray-400">Mid</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px]">
                        <div className="w-3 h-3 rounded-sm bg-green-500/80"></div>
                        <span className="text-gray-400">High</span>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    // Calendar-style heatmap: fixed 7 columns (Mon-Sun), variable rows (weeks).
                    const items: any[] = Array.isArray(dashboardSeries.list) ? dashboardSeries.list : []
                    const byDate = new Map<string, any>(items.map((d: any) => [String(d?.date ?? d?.key ?? ''), d]))

                    const parseYMD = (s: string) => {
                      // expects YYYY-MM-DD
                      const [y, m, d] = s.split('-').map((x) => parseInt(x, 10))
                      if (!y || !m || !d) return null
                      return new Date(y, m - 1, d)
                    }

                    const first = items[0]?.date || items[0]?.key
                    const last = items[items.length - 1]?.date || items[items.length - 1]?.key
                    const start = typeof first === 'string' ? parseYMD(first) : null
                    const end = typeof last === 'string' ? parseYMD(last) : null
                    if (!start || !end) return <div className="text-sm text-gray-400">No data</div>

                    const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                    const mondayIndex = (dt: Date) => (dt.getDay() + 6) % 7 // Mon=0..Sun=6

                    // Build cells (including leading blanks to align to Monday).
                    const cells: Array<{ date: string; data: any } | null> = []
                    for (let i = 0; i < mondayIndex(start); i++) cells.push(null)

                    const cursor = new Date(start)
                    while (cursor <= end) {
                      const y = cursor.getFullYear()
                      const m = String(cursor.getMonth() + 1).padStart(2, '0')
                      const dd = String(cursor.getDate()).padStart(2, '0')
                      const key = `${y}-${m}-${dd}`
                      cells.push({ date: key, data: byDate.get(key) })
                      cursor.setDate(cursor.getDate() + 1)
                    }

                    // Color based on conversion rate (Converted / Eligible): green = high, red = low
                    const colorForConversion = (converted: number, eligible: number) => {
                      if (eligible === 0) {
                        return { backgroundColor: 'rgba(75, 85, 99, 0.4)' } // gray for no data
                      }
                      const rate = Math.min(1, converted / eligible) // cap at 100%
                      // rate 0-1: red (0) -> yellow (0.5) -> green (1)
                      if (rate >= 0.7) {
                        return { backgroundColor: `rgba(34, 197, 94, ${0.6 + rate * 0.3})` } // green
                      } else if (rate >= 0.4) {
                        return { backgroundColor: `rgba(234, 179, 8, ${0.5 + rate * 0.3})` } // yellow
                      } else {
                        return { backgroundColor: `rgba(239, 68, 68, ${0.6 + (1 - rate) * 0.2})` } // red
                      }
                    }

                    return (
                      <div className="w-full relative">
                        <div className="grid grid-cols-7 gap-2 mb-2">
                          {weekdayLabels.map((d) => (
                            <div key={d} className="text-[11px] text-gray-400 text-center">
                              {d}
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-7 gap-2">
                          {cells.map((c, idx) => {
                            if (!c) {
                              return <div key={`empty-${idx}`} className="h-10 rounded-md bg-transparent" />
                            }
                            const total = Number(c.data?.total ?? 0)
                            const booked = Number(c.data?.booked ?? 0)
                            const arrived = Number(c.data?.arrived ?? 0)
                            const lost = Number(c.data?.lost ?? 0)
                            const denied = Number(c.data?.denied ?? 0)
                            const pending = Number(c.data?.booked_pending ?? 0)
                            const followUp = Number(c.data?.follow_up ?? 0)
                            const converted = booked + arrived
                            const eligible = total - denied
                            const convRate = eligible > 0 ? Math.round((converted / eligible) * 100) : 0
                            const isSelected = selectedPeakDay === c.date

                            return (
                              <div
                                key={c.date}
                                className={
                                  isSelected
                                    ? "h-10 rounded-md border border-accent-teal ring-2 ring-accent-teal/20 flex flex-col items-center justify-center text-[10px] cursor-pointer"
                                    : "h-10 rounded-md border border-dark-border flex flex-col items-center justify-center text-[10px] cursor-pointer"
                                }
                                style={colorForConversion(converted, eligible)}
                                title={`${c.date}\nConversion: ${convRate}%\nBooked: ${booked}\nArrived: ${arrived}\nPending: ${pending}\nFollow Up: ${followUp}\nLost: ${lost}\nDenied: ${denied}\nEligible: ${eligible}\nTotal: ${total}`}
                                onClick={() => {
                                  setSelectedPeakDay(c.date)
                                  grabLeadsForDay(c.date)
                                }}
                              >
                                <span className="text-white font-medium">{eligible > 0 ? `${convRate}%` : '-'}</span>
                                <span className="text-gray-300 text-[8px]">{c.date.slice(-2)}</span>
                              </div>
                            )
                          })}
                        </div>

                        {selectedPeakDay ? (() => {
                          const dayData = byDate.get(selectedPeakDay)
                          const dayTotal = Number(dayData?.total ?? 0)
                          const dayBooked = Number(dayData?.booked ?? 0)
                          const dayApproved = Number(dayData?.approved ?? 0)
                          const dayArrived = Number(dayData?.arrived ?? 0)
                          const dayLost = Number(dayData?.lost ?? 0)
                          const dayDenied = Number(dayData?.denied ?? 0)
                          const dayConverted = dayBooked + dayArrived
                          const dayEligible = dayTotal - dayDenied
                          const dayConv = dayEligible > 0 ? Math.round((dayConverted / dayEligible) * 100) : 0

                          return (
                            <div className="absolute right-0 top-0 mt-8 w-full sm:w-[360px] bg-dark-card border border-dark-border rounded-xl shadow-lg p-4 z-10">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-white font-semibold">{selectedPeakDay}</div>
                                  <div className="text-xs text-gray-400">Conversion breakdown</div>
                                </div>
                                <button
                                  className="text-sm text-gray-400 hover:text-white"
                                  onClick={() => {
                                    setSelectedPeakDay(null)
                                    setSelectedDayLeads([])
                                  }}
                                >
                                  ✕
                                </button>
                              </div>

                              {/* Mini Funnel Summary */}
                              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                                <div className="bg-dark-card-hover rounded-lg p-2">
                                  <div className="text-lg font-semibold text-white">{dayBooked}</div>
                                  <div className="text-[10px] text-gray-400">Booked</div>
                                </div>
                                <div className="bg-dark-card-hover rounded-lg p-2">
                                  <div className="text-lg font-semibold text-green-400">{dayApproved}</div>
                                  <div className="text-[10px] text-gray-400">Approved</div>
                                </div>
                                <div className="bg-dark-card-hover rounded-lg p-2">
                                  <div className="text-lg font-semibold text-accent-teal">{dayArrived}</div>
                                  <div className="text-[10px] text-gray-400">Arrived</div>
                                </div>
                                <div className={`rounded-lg p-2 ${dayConv >= 70 ? 'bg-green-500/20' : dayConv >= 40 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                                  <div className={`text-lg font-semibold ${dayConv >= 70 ? 'text-green-400' : dayConv >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>{dayConv}%</div>
                                  <div className="text-[10px] text-gray-400">Conv</div>
                                </div>
                              </div>

                              {/* Leads List - Filtered to show booked/approved/arrived */}
                              <div className="mt-3 max-h-[200px] overflow-auto">
                                {selectedDayLoading ? (
                                  <div className="text-sm text-gray-400">Loading leads…</div>
                                ) : selectedDayLeads.length === 0 ? (
                                  <div className="text-sm text-gray-400">No leads for this day.</div>
                                ) : (
                                  <div className="space-y-2">
                                    {selectedDayLeads
                                      .filter((l: any) => ['Booked', 'Approved', 'Arrived'].includes(normalizeLeadStage(l.stage)))
                                      .map((l: any) => {
                                        const stage = normalizeLeadStage(l.stage)
                                        const stageColor = stage === 'Arrived' ? 'bg-accent-teal/20 text-accent-teal'
                                          : stage === 'Approved' ? 'bg-green-500/20 text-green-400'
                                          : 'bg-blue-500/20 text-blue-400'
                                        return (
                                          <div
                                            key={l.intake_id}
                                            className="rounded-lg border border-dark-border p-2 bg-dark-card-hover"
                                          >
                                            <div className="flex items-center justify-between gap-2">
                                              <div className="text-sm text-white font-medium truncate">
                                                {l.name}
                                              </div>
                                              <div className={`text-[10px] px-2 py-0.5 rounded-full ${stageColor}`}>
                                                {stage}
                                              </div>
                                            </div>
                                            <div className="mt-1 text-xs text-gray-400">
                                              {l.facility || 'No facility'}
                                            </div>
                                          </div>
                                        )
                                      })}
                                    {selectedDayLeads.filter((l: any) => ['Booked', 'Approved', 'Arrived'].includes(normalizeLeadStage(l.stage))).length === 0 && (
                                      <div className="text-sm text-gray-400">No booked/approved/arrived leads this day.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })() : null}
                      </div>
                    )
                  })()}
                </div>
              </div>

              {/* Call Volume Chart */}
              <div className="mt-6 bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-white font-semibold">Call Volume</div>
                  <div className="text-xs text-gray-400">{dashboardRangeLabel}</div>
                </div>
                {callLoading ? (
                  <div className="text-sm text-gray-400">Loading call data...</div>
                ) : callError ? (
                  <div className="text-sm text-red-500">{callError}</div>
                ) : callMetrics.timeseries.length === 0 ? (
                  <div className="text-sm text-gray-400">No call data available</div>
                ) : (() => {
                  const callBarData = callMetrics.timeseries.map((d: any) => ({
                    ...d,
                    total_calls: d.isForecast ? null : (
                      (hiddenCallBars.has('total_answered') ? 0 : d.total_answered) +
                      (hiddenCallBars.has('total_outbound') ? 0 : d.total_outbound)
                    ),
                    total_answered: d.isForecast ? null : d.total_answered,
                    total_outbound: d.isForecast ? null : d.total_outbound,
                    forecast_calls: d.isForecast ? (d.total_answered + d.total_outbound) : null,
                  }))
                  // Bridge last historical to forecast
                  const lastHistIdx = callBarData.reduce((acc: number, d: any, i: number) => !d.isForecast ? i : acc, -1)
                  if (lastHistIdx >= 0 && callBarData[lastHistIdx]) {
                    callBarData[lastHistIdx].forecast_calls = callBarData[lastHistIdx].total_calls
                  }
                  const callLastDate = lastHistIdx >= 0 ? callBarData[lastHistIdx]?.label : null
                  return (
                  <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={callBarData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.4)" />
                        <XAxis dataKey="label" tick={{ fill: 'grey' }} />
                        <YAxis tick={{ fill: 'grey' }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                          formatter={(value: any, name: string, props: any) => {
                            const label = props?.payload?.isForecast ? `${value} (projected)` : value
                            return [label, name]
                          }}
                        />
                        <Legend
                          onClick={(e) => {
                            const dataKey = e.dataKey as string
                            if (dataKey.startsWith('forecast_')) return
                            setHiddenCallBars(prev => {
                              const next = new Set(prev)
                              if (next.has(dataKey)) {
                                next.delete(dataKey)
                              } else {
                                next.add(dataKey)
                              }
                              return next
                            })
                          }}
                          formatter={(value, entry: any) => (
                            <span style={{
                              color: hiddenCallBars.has(entry.dataKey) ? '#6b7280' : '#fff',
                              textDecoration: hiddenCallBars.has(entry.dataKey) ? 'line-through' : 'none',
                              cursor: 'pointer'
                            }}>
                              {value}
                            </span>
                          )}
                        />
                        <Bar dataKey="total_calls" name="Total Calls" fill="#6366f1" radius={[4, 4, 0, 0]} hide={hiddenCallBars.has('total_calls')} />
                        <Bar dataKey="total_answered" name="Answered" fill="#10b981" radius={[4, 4, 0, 0]} hide={hiddenCallBars.has('total_answered')} />
                        <Bar dataKey="total_outbound" name="Outbound" fill="#f59e0b" radius={[4, 4, 0, 0]} hide={hiddenCallBars.has('total_outbound')} />
                        <Area dataKey="forecast_calls" name="Call Forecast" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} strokeDasharray="5 5" strokeWidth={2} connectNulls={false} type="monotone" />
                        {callLastDate && <ReferenceLine x={callLastDate} stroke="#6b7280" strokeDasharray="3 3" label={{ value: 'Forecast', fill: '#9ca3af', fontSize: 11, position: 'top' }} />}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  )
                })()}
              </div>

              <div className="mt-6">
                <div className="bg-dark-card border border-dark-border rounded-xl shadow-sm">
                  <div className="flex items-center justify-between px-6 pt-5">
                    <div className="text-white font-semibold">Breakdown</div>
                    <div className="text-xs text-gray-400">Totals</div>
                  </div>

                  <div className="px-6 mt-4 flex items-center gap-2 flex-wrap">
                {(['Facilities', 'Performance & Calls', 'Leads', 'Ring Group', 'Census'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setBreakdownTab(t)}
                        className={
                          breakdownTab === t
                            ? 'px-3 py-1.5 rounded-md text-sm bg-accent-teal text-white'
                            : 'px-3 py-1.5 rounded-md text-sm bg-dark-card-hover text-gray-200 border border-dark-border'
                        }
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  <div className="p-6 pt-4">
                {crmLoading ? (
                  <div className="text-sm text-gray-400">Loading...</div>
                ) : crmError ? (
                  <div className="text-sm text-red-500">{crmError}</div>
                ) : breakdownTab === 'Facilities' ? (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                    <div className="text-white font-semibold mb-4">By Facility</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-dark-border">
                            {sortTh("facilities", "facility", "Facility")}
                            {sortTh("facilities", "eligible", "Approved Leads")}
                            {sortTh("facilities", "booked", "Booked")}
                            {sortTh("facilities", "arrived", "Arrived")}
                            {sortTh("facilities", "conv", "Conv %")}
                          </tr>
                        </thead>
                        <tbody>
                          {sortRows('facilities', (crmDashboard?.by_facility || []), (r: any, k: string) => {
                            const total = Number(r.total ?? 0)
                            const denied = Number(r.denied ?? 0)
                            const eligible = total - denied
                            const booked = Number(r.booked ?? 0)
                            const arrived = Number(r.arrived ?? 0)
                            const conv = eligible > 0 ? Math.round(((booked + arrived) / eligible) * 100) : 0
                            if (k === 'facility') return r.facility
                            if (k === 'eligible') return eligible
                            if (k === 'booked') return booked
                            if (k === 'arrived') return arrived
                            if (k === 'conv') return conv
                            return r[k]
                          }).map((r: any) => {
                            const total = Number(r.total ?? 0)
                            const denied = Number(r.denied ?? 0)
                            const eligible = total - denied
                            const booked = Number(r.booked ?? 0)
                            const arrived = Number(r.arrived ?? 0)
                            const converted = booked + arrived
                            const conv = eligible > 0 ? Math.round((converted / eligible) * 100) : 0
                            return (
                              <tr key={r.facility} className="border-b border-dark-border">
                                <td className="py-2 pr-4 text-white">{r.facility}</td>
                                <td className="py-2 pr-4 text-accent-teal">{eligible}</td>
                                <td className="py-2 pr-4 text-white">{booked}</td>
                                <td className="py-2 pr-4 text-white">{arrived}</td>
                                <td className="py-2 pr-4 text-accent-teal">{conv}%</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : breakdownTab === 'Performance & Calls' ? (
                  <div className="space-y-6">
                    {/* Performance Table */}
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                      <div className="text-white font-semibold mb-4">Performance by Coordinator</div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-400 border-b border-dark-border">
                              {sortTh("perfCoord", "coordinator_name", "Coordinator")}
                              {sortTh("perfCoord", "eligible", "Approved Leads")}
                              {sortTh("perfCoord", "booked", "Booked")}
                              {sortTh("perfCoord", "arrived", "Arrived")}
                              {sortTh("perfCoord", "conv", "Conv %")}
                            </tr>
                          </thead>
                          <tbody>
                            {sortRows('perfCoord', (crmDashboard?.by_coordinator || []), (r: any, k: string) => {
                              const eligible = Number(r.total ?? 0) - Number(r.denied ?? 0)
                              const booked = Number(r.booked ?? 0)
                              const arrived = Number(r.arrived ?? 0)
                              const conv = eligible > 0 ? Math.round(((booked + arrived) / eligible) * 100) : 0
                              if (k === 'eligible') return eligible
                              if (k === 'conv') return conv
                              return r[k]
                            }).map((r: any) => {
                              const total = Number(r.total ?? 0)
                              const denied = Number(r.denied ?? 0)
                              const eligible = total - denied
                              const booked = Number(r.booked ?? 0)
                              const arrived = Number(r.arrived ?? 0)
                              const converted = booked + arrived
                              const conv = eligible > 0 ? Math.round((converted / eligible) * 100) : 0
                              return (
                                <tr key={r.coordinator_id || r.coordinator_name} className="border-b border-dark-border">
                                  <td className="py-2 pr-4 text-white">
                                    {r.coordinator_name || 'Unknown'}
                                  </td>
                                  <td className="py-2 pr-4 text-accent-teal">{eligible}</td>
                                  <td className="py-2 pr-4 text-white">{booked}</td>
                                  <td className="py-2 pr-4 text-white">{arrived}</td>
                                  <td className="py-2 pr-4 text-accent-teal">{conv}%</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Calls Table */}
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                      <div className="text-white font-semibold mb-4">Calls by Coordinator</div>
                      {callLoading ? (
                        <div className="text-sm text-gray-400">Loading call data...</div>
                      ) : callError ? (
                        <div className="text-sm text-red-500">{callError}</div>
                      ) : callMetrics.byCoordinator.length === 0 ? (
                        <div className="text-sm text-gray-400">No coordinator call data available</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-400 border-b border-dark-border">
                                {sortTh("callsCoord", "coordinator_name", "Coordinator")}
                                {sortTh("callsCoord", "total_answered", "Answered")}
                                {sortTh("callsCoord", "total_outbound", "Outbound")}
                                {sortTh("callsCoord", "avg_talk_time_mins", "Avg Talk (min)")}
                                {sortTh("callsCoord", "total_talk_time_mins", "Total Talk (min)")}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b-2 border-dark-border font-semibold bg-dark-bg/50">
                                <td className="py-2 pr-4 text-white">Total</td>
                                <td className="py-2 pr-4 text-accent-teal">
                                  {callMetrics.byCoordinator.reduce((sum: number, c: any) => sum + (c.total_answered || 0), 0)}
                                </td>
                                <td className="py-2 pr-4 text-white">
                                  {callMetrics.byCoordinator.reduce((sum: number, c: any) => sum + (c.total_outbound || 0), 0)}
                                </td>
                                <td className="py-2 pr-4 text-white">-</td>
                                <td className="py-2 pr-4 text-white">
                                  {callMetrics.byCoordinator.reduce((sum: number, c: any) => sum + (c.total_talk_time_mins || 0), 0).toFixed(1)}
                                </td>
                              </tr>
                              {sortRows('callsCoord', callMetrics.byCoordinator).map((coord: any, idx: number) => (
                                <tr key={idx} className="border-b border-dark-border">
                                  <td className="py-2 pr-4 text-white">{coord.coordinator_name}</td>
                                  <td className="py-2 pr-4 text-accent-teal">{coord.total_answered}</td>
                                  <td className="py-2 pr-4 text-white">{coord.total_outbound}</td>
                                  <td className="py-2 pr-4 text-white">{coord.avg_talk_time_mins?.toFixed(1) || 0}</td>
                                  <td className="py-2 pr-4 text-white">{coord.total_talk_time_mins?.toFixed(1) || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : breakdownTab === 'Leads' ? (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                    <div className="text-white font-semibold mb-4">Latest Leads</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-dark-border">
                            {sortTh("leads", "name", "Name")}
                            {sortTh("leads", "stage", "Stage")}
                            {sortTh("leads", "source", "Source")}
                            {sortTh("leads", "facility", "Facility")}
                            {sortTh("leads", "last_note_date", "Last touch")}
                          </tr>
                        </thead>
                        <tbody>
                          {sortRows('leads', (crmLeads || [])).map((r: any) => (
                            <tr key={r.intake_id} className="border-b border-dark-border">
                              <td className="py-2 pr-4 text-white">{r.name}</td>
                              <td className="py-2 pr-4 text-accent-teal">{normalizeLeadStage(r.stage)}</td>
                              <td className="py-2 pr-4 text-white">{r.source || 'Unknown'}</td>
                              <td className="py-2 pr-4 text-white">{r.facility}</td>
                              <td className="py-2 pr-4 text-white">{r.last_note_date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : breakdownTab === 'Ring Group' ? (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                    <div className="text-white font-semibold mb-4">Call Activity by Ring Group</div>
                    {callLoading ? (
                      <div className="text-sm text-gray-400">Loading call data...</div>
                    ) : callMetrics.byRingGroup.length === 0 ? (
                      <div className="text-sm text-gray-400">No ring group data available</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-400 border-b border-dark-border">
                              {sortTh("ringGroup", "ring_group", "Ring Group")}
                              {sortTh("ringGroup", "total_answered", "Inbound Answered")}
                              {sortTh("ringGroup", "total_leads", "Leads")}
                              {sortTh("ringGroup", "total_booked", "Booked / Arrived")}
                              {sortTh("ringGroup", "total_talk_time_mins", "Talk Time (min)")}
                              {sortTh("ringGroup", "avg_talk_time_mins", "Avg Talk (min)")}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b-2 border-dark-border font-semibold bg-dark-bg/50">
                              <td className="py-2 pr-4 text-white">Total</td>
                              <td className="py-2 pr-4 text-accent-teal">
                                {callMetrics.byRingGroup.reduce((sum: number, g: any) => sum + (g.total_answered || 0), 0)}
                              </td>
                              <td className="py-2 pr-4 text-accent-teal">
                                {callMetrics.byRingGroup.reduce((sum: number, g: any) => sum + (g.total_leads || 0), 0)}
                              </td>
                              <td className="py-2 pr-4 text-accent-teal">
                                {callMetrics.byRingGroup.reduce((sum: number, g: any) => sum + (g.total_booked || 0), 0)}
                                {' / '}
                                {callMetrics.byRingGroup.reduce((sum: number, g: any) => sum + (g.total_arrived || 0), 0)}
                              </td>
                              <td className="py-2 pr-4 text-white">
                                {callMetrics.byRingGroup.reduce((sum: number, g: any) => sum + (g.total_talk_time_mins || 0), 0).toFixed(1)}
                              </td>
                              <td className="py-2 pr-4 text-white">-</td>
                            </tr>
                            {sortRows('ringGroup', callMetrics.byRingGroup).map((group: any, idx: number) => (
                              <tr key={idx} className="border-b border-dark-border">
                                <td className="py-2 pr-4 text-white">{group.ring_group}</td>
                                <td className="py-2 pr-4 text-accent-teal">{group.total_answered || 0}</td>
                                <td className="py-2 pr-4 text-accent-teal">{group.total_leads || 0}</td>
                                <td className="py-2 pr-4 text-accent-teal">{group.total_booked || 0} / {group.total_arrived || 0}</td>
                                <td className="py-2 pr-4 text-white">{group.total_talk_time_mins?.toFixed(1) || 0}</td>
                                <td className="py-2 pr-4 text-white">{group.avg_talk_time_mins?.toFixed(1) || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : breakdownTab === 'Census' ? (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm">
                    <div className="text-white font-semibold mb-4">Census by Facility</div>
                    {censusLoading ? (
                      <div className="text-sm text-gray-400">Loading census data...</div>
                    ) : !censusAnalytics?.by_facility?.length ? (
                      <div className="text-sm text-gray-400">No facility data available</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-400 border-b border-dark-border">
                              {sortTh("census", "facility", "Facility")}
                              {sortTh("census", "total_patients", "Total")}
                              {sortTh("census", "rtc", "RTC")}
                              {sortTh("census", "php", "PHP")}
                              {sortTh("census", "detox", "Detox")}
                              {sortTh("census", "iop", "IOP")}
                            </tr>
                          </thead>
                          <tbody>
                            {sortRows('census', censusAnalytics.by_facility).map((fac: any, idx: number) => (
                              <tr key={idx} className="border-b border-dark-border">
                                <td className="py-2 pr-4 text-white">{fac.facility}</td>
                                <td className="py-2 pr-4 text-accent-teal">{fac.total_patients}</td>
                                <td className="py-2 pr-4 text-white">{fac.rtc || 0}</td>
                                <td className="py-2 pr-4 text-white">{fac.php || 0}</td>
                                <td className="py-2 pr-4 text-white">{fac.detox || 0}</td>
                                <td className="py-2 pr-4 text-white">{fac.iop || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Discharge Reasons */}
                    {censusAnalytics?.discharge_summary && (
                      <div className="mt-8">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-white font-semibold">Discharge Reasons</div>
                          <div className="text-xs text-gray-400">
                            By discharge date · {dashboardRangeLabel}
                          </div>
                        </div>

                        {/* KPI strip */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                          <div className="bg-dark-bg/60 border border-dark-border rounded-lg px-4 py-3">
                            <div className="text-xs text-gray-400">Total in Care</div>
                            <div className="text-2xl font-semibold text-white mt-1">
                              {censusAnalytics.discharge_summary.total_in_care}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Active + discharged in period</div>
                          </div>
                          <div className="bg-dark-bg/60 border border-dark-border rounded-lg px-4 py-3">
                            <div className="text-xs text-gray-400">Discharged</div>
                            <div className="text-2xl font-semibold text-accent-teal mt-1">
                              {censusAnalytics.discharge_summary.discharged}
                              <span className="text-sm text-gray-400 font-normal ml-2">
                                ({censusAnalytics.discharge_summary.discharged_pct}%)
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Left during the period</div>
                          </div>
                          <div className="bg-dark-bg/60 border border-dark-border rounded-lg px-4 py-3">
                            <div className="text-xs text-gray-400">Still Active</div>
                            <div className="text-2xl font-semibold text-white mt-1">
                              {censusAnalytics.discharge_summary.active}
                              <span className="text-sm text-gray-400 font-normal ml-2">
                                ({censusAnalytics.discharge_summary.active_pct}%)
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Currently in treatment</div>
                          </div>
                        </div>

                        {/* Reason summary */}
                        {(censusAnalytics.by_discharge_reason || []).length === 0 ? (
                          <div className="text-sm text-gray-400">No discharges in this period</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-400 border-b border-dark-border">
                                  <th className="py-2 pr-4 font-medium">Reason</th>
                                  <th className="py-2 pr-4 font-medium w-1/3">Count</th>
                                  <th className="py-2 pr-4 font-medium">% of DCs</th>
                                  <th className="py-2 pr-4 font-medium">Avg Days</th>
                                  <th className="py-2 pr-4 font-medium">Min</th>
                                  <th className="py-2 pr-4 font-medium">Max</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const maxCount = Math.max(...censusAnalytics.by_discharge_reason.map((r: any) => r.count), 1)
                                  return censusAnalytics.by_discharge_reason.map((r: any, idx: number) => {
                                    const pctBar = (r.count / maxCount) * 100
                                    const isAcaAdmin = r.dc_reason === 'Left Voluntarily/ACA' || r.dc_reason === 'Administrative Discharged'
                                    return (
                                      <tr
                                        key={idx}
                                        className={`border-b border-dark-border cursor-pointer hover:bg-dark-bg/40 ${dcReasonFilter === r.dc_reason ? 'bg-dark-bg/60' : ''}`}
                                        onClick={() => {
                                          setDcReasonFilter(dcReasonFilter === r.dc_reason ? 'All' : r.dc_reason)
                                          setShowDcDetail(true)
                                        }}
                                      >
                                        <td className="py-2 pr-4 text-white">
                                          {r.dc_reason}
                                          {isAcaAdmin && <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">ACA/Admin</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-accent-teal">
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-dark-bg/80 rounded overflow-hidden min-w-[60px]">
                                              <div className="h-full bg-accent-teal/80" style={{ width: `${pctBar}%` }} />
                                            </div>
                                            <span className="w-8 text-right">{r.count}</span>
                                          </div>
                                        </td>
                                        <td className="py-2 pr-4 text-white">{r.pct_of_dcs}%</td>
                                        <td className="py-2 pr-4 text-white">{r.avg_days}</td>
                                        <td className="py-2 pr-4 text-gray-400">{r.min_days}</td>
                                        <td className="py-2 pr-4 text-gray-400">{r.max_days}</td>
                                      </tr>
                                    )
                                  })
                                })()}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Detail toggle + filter status */}
                        {(censusAnalytics.discharges || []).length > 0 && (
                          <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                            <button
                              onClick={() => setShowDcDetail(s => !s)}
                              className="text-xs text-accent-teal hover:underline"
                            >
                              {showDcDetail ? '▼ Hide' : '▶ Show'} client detail ({censusAnalytics.discharges.length})
                            </button>
                            {dcReasonFilter !== 'All' && (
                              <div className="text-xs text-gray-400">
                                Filtered: <span className="text-white">{dcReasonFilter}</span>
                                <button
                                  onClick={() => setDcReasonFilter('All')}
                                  className="ml-2 text-accent-teal hover:underline"
                                >
                                  clear
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Per-client detail */}
                        {showDcDetail && (censusAnalytics.discharges || []).length > 0 && (
                          <div className="mt-3 overflow-x-auto max-h-96 overflow-y-auto border border-dark-border rounded-lg">
                            <table className="min-w-full text-sm">
                              <thead className="sticky top-0 bg-dark-card">
                                <tr className="text-left text-gray-400 border-b border-dark-border">
                                  <th className="py-2 px-3 font-medium">Client</th>
                                  <th className="py-2 px-3 font-medium">Facility</th>
                                  <th className="py-2 px-3 font-medium">Admit</th>
                                  <th className="py-2 px-3 font-medium">Discharge</th>
                                  <th className="py-2 px-3 font-medium">Days</th>
                                  <th className="py-2 px-3 font-medium">Reason</th>
                                  <th className="py-2 px-3 font-medium">Insurance</th>
                                  <th className="py-2 px-3 font-medium">LOC</th>
                                </tr>
                              </thead>
                              <tbody>
                                {censusAnalytics.discharges
                                  .filter((d: any) => dcReasonFilter === 'All' || d.dc_reason === dcReasonFilter)
                                  .map((d: any, idx: number) => (
                                    <tr key={idx} className="border-b border-dark-border/60">
                                      <td className="py-2 px-3 text-white">{d.patient_name}</td>
                                      <td className="py-2 px-3 text-gray-300">{d.facility || '-'}</td>
                                      <td className="py-2 px-3 text-gray-300">{d.admission_date || '-'}</td>
                                      <td className="py-2 px-3 text-gray-300">{d.discharge_date || '-'}</td>
                                      <td className="py-2 px-3 text-accent-teal">{d.days_stayed}</td>
                                      <td className="py-2 px-3 text-white">{d.dc_reason}</td>
                                      <td className="py-2 px-3 text-gray-300">{d.insurance || '-'}</td>
                                      <td className="py-2 px-3 text-gray-300">{d.level_of_care || '-'}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                  </div>
                </div>
              </div>
              </>
              )}
              </>
              )}

              {/* Deposits Tab Content */}
              {mainTab === 'Deposits' && (
                <div className="mt-6">
                  {(depositLoading || bankBalanceLoading || expenseLoading) ? (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">Loading deposit data...</div>
                    </div>
                  ) : depositData ? (
                    <>
                      {/* Period Cash Flow Waterfall */}
                      <div className="bg-dark-card border border-dark-border rounded-xl p-8 mb-4">
                        <div className="text-xs uppercase tracking-wider text-gray-400 mb-5">Period Cash Flow</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-green-500/15 flex items-center justify-center">
                              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400 flex items-center gap-1.5">
                                Inflows
                                <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                  <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                    Real money that entered PHG during this period: insurance ERAs, patient payments, etc. Excludes cosmetic inter-account sweeps (same dollars showing as a withdrawal on another PHG account).
                                  </span>
                                </span>
                              </div>
                              <div className="text-3xl font-bold text-white">{formatDollar(depositData.cash_flow?.total_inflows || 0)}</div>
                              <div className="text-xs text-gray-500 mt-0.5">real cash entered PHG</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-red-500/15 flex items-center justify-center">
                              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400 flex items-center gap-1.5">
                                Outflows
                                <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                  <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                    Real money that left PHG during this period: payroll, vendors, rent, taxes, plus withdrawals to non-Plaid accounts. Excludes cosmetic inter-account sweeps (same dollars showing as a deposit on another PHG account).
                                  </span>
                                </span>
                              </div>
                              <div className="text-3xl font-bold text-white">{formatDollar(depositData.cash_flow?.total_outflows || 0)}</div>
                              <div className="text-xs text-gray-500 mt-0.5">real cash left PHG</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              (depositData.cash_flow?.net_change || 0) >= 0 ? 'bg-accent-teal/15' : 'bg-orange-500/15'
                            }`}>
                              <svg className={`w-6 h-6 ${(depositData.cash_flow?.net_change || 0) >= 0 ? 'text-accent-teal' : 'text-orange-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400 flex items-center gap-1.5">
                                Net Change
                                <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                  <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                    Inflows minus Outflows over the period. Positive = your cash position grew. Negative = you spent more than you took in. Equals the change in Current Balance from start of period to today.
                                  </span>
                                </span>
                              </div>
                              <div className={`text-3xl font-bold ${(depositData.cash_flow?.net_change || 0) >= 0 ? 'text-accent-teal' : 'text-orange-400'}`}>
                                {(depositData.cash_flow?.net_change || 0) >= 0 ? '+' : ''}{formatDollar(depositData.cash_flow?.net_change || 0)}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">change in cash this period</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Snapshot Strip: Current Balance + ERA */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="bg-dark-card border border-dark-border rounded-xl p-5 flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-accent-teal/15 flex items-center justify-center">
                            <svg className="w-6 h-6 text-accent-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                              Current Balance <span className="text-[10px] normal-case text-gray-500">(today)</span>
                              <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                  Sum of cash in all Plaid-connected depository accounts right now. Point-in-time snapshot — does NOT change with the date filter. Answers "how much cash do we have today."
                                </span>
                              </span>
                            </div>
                            <div className="text-2xl font-bold text-white">{formatDollar(bankBalance)}</div>
                          </div>
                        </div>
                        {depositData.total_era > 0 && (
                          <div className="bg-dark-card border border-dark-border rounded-xl p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-green-500/15 flex items-center justify-center">
                              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                                ERA Payments <span className="text-[10px] normal-case text-gray-500">(period)</span>
                                <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                  <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                    Total dollars insurance payers paid you over the period (from 835 ERA remittances). Payer's binding statement of what they paid; the actual EFT to your bank usually lands 1–3 business days later and may be batched across multiple ERAs.
                                  </span>
                                </span>
                              </div>
                              <div className="text-2xl font-bold text-white">{formatDollar(depositData.total_era)} <span className="text-sm font-normal text-gray-400">· {depositData.era_count.toLocaleString()} claims</span></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Two Column Layout: Deposits by Account & ERA by Payer */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Deposits by Account */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Deposits by Account</div>
                          {depositData.by_account?.length > 0 ? (
                            <div className="space-y-3">
                              {depositData.by_account.map((acc: any, idx: number) => {
                                const pct = depositData.total_deposits > 0 ? (acc.total_amount / depositData.total_deposits) * 100 : 0
                                const colors = ['bg-accent-teal', 'bg-green-500', 'bg-blue-500', 'bg-purple-500']
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white truncate">{acc.account_name}</span>
                                      <span className="text-accent-teal font-medium">${Math.round(acc.total_amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}% • {acc.deposit_count} deposits</div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No account data</div>
                          )}
                        </div>

                        {/* ERA by Payer */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">ERA by Insurance Payer</div>
                          {depositData.era_by_payer?.length > 0 ? (
                            <div className="space-y-3">
                              {depositData.era_by_payer.slice(0, 6).map((payer: any, idx: number) => {
                                const pct = depositData.total_era > 0 ? (payer.total_amount / depositData.total_era) * 100 : 0
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white truncate">{payer.payer_name || 'Unknown'}</span>
                                      <span className="text-green-400 font-medium">${Math.round(payer.total_amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}% • {payer.claim_count} claims</div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No ERA payer data</div>
                          )}
                        </div>
                      </div>

                      {/* Peak Deposit Days */}
                      {depositData.top_deposit_days?.length > 0 && (
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Peak Deposit Days</div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {depositData.top_deposit_days.map((day: any, idx: number) => (
                              <div key={idx} className="bg-dark-card-hover rounded-lg p-4 border border-dark-border">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`text-sm font-medium ${idx === 0 ? 'text-accent-teal' : idx === 1 ? 'text-green-400' : 'text-blue-400'}`}>
                                    {idx === 0 ? '1st' : idx === 1 ? '2nd' : '3rd'}
                                  </span>
                                  <span className="text-white text-sm">
                                    {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                                <div className="text-2xl font-bold text-accent-teal">${Math.round(day.deposits).toLocaleString()}</div>
                                <div className="text-xs text-gray-400">{day.count} deposits</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-10 flex flex-col items-center justify-center gap-4">
                      <div className="text-gray-400 text-lg">No deposit data available</div>
                      <div className="text-gray-500 text-sm">Connect a US bank account via Plaid to see deposits, expenses, and balances.</div>
                      <button
                        onClick={() => openPlaidLink()}
                        disabled={!plaidReady}
                        className="mt-2 px-6 py-3 rounded-lg text-sm font-medium bg-accent-teal text-white hover:bg-accent-teal/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Connect Bank Account
                      </button>
                    </div>
                  )}
                  {depositData && linkToken && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => openPlaidLink()}
                        disabled={!plaidReady}
                        className="px-4 py-2 rounded-lg text-xs font-medium bg-dark-card-hover text-gray-300 border border-dark-border hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Link Another Account
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Expenses Tab Content */}
              {mainTab === 'Expenses' && (
                <div className="mt-6">
                  {expenseLoading ? (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">Loading expense data...</div>
                    </div>
                  ) : expenseData ? (
                    <>
                      {/* Executive Summary KPIs */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                        <StatCard
                          label="Total Expenses"
                          value={formatDollar(expenseData.total_expenses || 0)}
                          sublabel={dashboardRangeLabel}
                          tooltip="Actual business spending: payroll, vendors, rent, taxes, etc. Excludes internal transfers between your own accounts."
                        />
                        <StatCard
                          label="Daily Average"
                          value={`$${Math.round(expenseData.daily_average || 0).toLocaleString()}`}
                          sublabel="Per day"
                          tooltip="Average daily spending calculated by dividing total expenses by the number of active days."
                        />
                        <StatCard
                          label="Transactions"
                          value={expenseData.transaction_count || 0}
                          sublabel={`${(expenseData.by_category || []).length} categories`}
                          tooltip="Total number of expense transactions across all accounts, grouped into spending categories."
                        />
                        <StatCard
                          label="Top Category"
                          value={expenseData.top_category?.name || 'N/A'}
                          sublabel={`$${Math.round(expenseData.top_category?.amount || 0).toLocaleString()}`}
                          tooltip="The spending category with the highest total amount for this period."
                        />
                      </div>

                      {/* Cash Outflow Roll-up: Expenses + Withdrawals = Total Outflows (transfers excluded) */}
                      {(expenseData.internal_transfers?.total > 0 || expenseData.total_outflows > 0) && (
                        <div className="bg-dark-card border border-dark-border rounded-xl p-5 mb-6">
                          <div className="text-xs uppercase tracking-wider text-gray-400 mb-4">Cash Outflow Reconciliation</div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="flex items-center justify-between sm:justify-start sm:gap-4">
                              <div>
                                <div className="text-xs text-gray-400">Business Expenses</div>
                                <div className="text-xl font-bold text-red-400">{formatDollar(expenseData.total_expenses || 0)}</div>
                                <div className="text-[10px] text-gray-500 mt-0.5">real spending</div>
                              </div>
                              <div className="text-2xl text-gray-600 font-light hidden sm:block">+</div>
                            </div>
                            <div className="flex items-center justify-between sm:justify-start sm:gap-4">
                              <div>
                                <div className="text-xs text-yellow-300 flex items-center gap-1.5">
                                  Withdrawals
                                  <span className="relative inline-flex group cursor-help text-gray-500 hover:text-yellow-300">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                      Transfers that left your connected accounts but DID NOT land in any other Plaid-connected PHG account. Likely owner draws, transfers to a non-Plaid bank, or money that genuinely walked out of the visible system.
                                    </span>
                                  </span>
                                </div>
                                <div className="text-xl font-bold text-yellow-400">{formatDollar(expenseData.internal_transfers?.unmatched_withdrawals || 0)}</div>
                                <div className="text-[10px] text-gray-500 mt-0.5">left visible system</div>
                              </div>
                              <div className="text-2xl text-gray-600 font-light hidden sm:block">=</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400">Total Outflows</div>
                              <div className="text-xl font-bold text-orange-400">{formatDollar(expenseData.total_outflows || 0)}</div>
                              <div className="text-[10px] text-gray-500 mt-0.5">real cash that left PHG</div>
                            </div>
                          </div>
                          {/* Informational only: inter-account sweeps don't change PHG cash total */}
                          {(expenseData.internal_transfers?.matched || 0) > 0 && (
                            <div className="mt-4 pt-4 border-t border-dark-border">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                    Inter-Account Transfers (info only)
                                    <span className="relative inline-flex group cursor-help text-gray-500 hover:text-accent-teal">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                      <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-72 bg-gray-900 text-white text-xs font-normal normal-case rounded-lg p-3 shadow-xl border border-dark-border z-50 pointer-events-none">
                                        Money swept between your own PHG accounts — same dollars left one account and landed in another. Net zero impact on total PHG cash, so excluded from Total Outflows.
                                      </span>
                                    </span>
                                  </div>
                                </div>
                                <div className="text-sm font-medium text-gray-400">{formatDollar(expenseData.internal_transfers?.matched || 0)}</div>
                              </div>
                              {expenseData.internal_transfers?.by_account?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {expenseData.internal_transfers.by_account.slice(0, 5).map((acc: any, idx: number) => (
                                    <div key={idx} className="flex items-center justify-between text-[11px]">
                                      <span className="text-gray-500 truncate">{acc.account_name}</span>
                                      <span className="text-gray-500 ml-3 whitespace-nowrap">{formatDollar(acc.total_amount)} <span className="text-gray-600">· {acc.transfer_count}</span></span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Narrative Insight */}
                      <div className="bg-gradient-to-r from-dark-card to-dark-card-hover border border-dark-border rounded-xl p-5 mb-6">
                        <div>
                          <div className="text-white font-semibold mb-1">Expense Tracking Summary</div>
                          <div className="text-sm text-gray-300 leading-relaxed">
                            {expenseData.total_expenses > 0 ? (
                              <>
                                Over {dashboardRangeLabel.toLowerCase()}, <span className="text-red-400 font-medium">${Math.round(expenseData.total_expenses).toLocaleString()}</span> was spent across {expenseData.transaction_count} transactions.
                                {expenseData.top_category && (
                                  <> <span className="text-white font-medium">{expenseData.top_category.name}</span> was the largest category at ${Math.round(expenseData.top_category.amount).toLocaleString()} ({Math.round((expenseData.top_category.amount / expenseData.total_expenses) * 100)}% of total). </>
                                )}
                                {expenseData.top_merchants?.[0] && (
                                  <>Top vendor: <span className="text-white font-medium">{expenseData.top_merchants[0].merchant}</span> with ${Math.round(expenseData.top_merchants[0].total_amount).toLocaleString()}. </>
                                )}
                                Daily average spending is <span className="text-orange-400 font-medium">${Math.round(expenseData.daily_average).toLocaleString()}</span>.
                              </>
                            ) : (
                              'No expense activity recorded for this period.'
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Daily Trend Chart */}
                      <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-white font-semibold">Daily Spending Trend</div>
                            <div className="text-xs text-gray-400">Track expense patterns over time</div>
                          </div>
                        </div>
                        {expenseData.timeseries?.length > 0 ? (
                          (() => {
                          const { data: expenseChartData, lastHistoricalDate: expenseLastDate } = prepareForecastData(expenseData.timeseries, ['expenses'])
                          return (
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={expenseChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                              <XAxis
                                dataKey="date"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              />
                              <YAxis
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                              />
                              <Tooltip content={<ForecastTooltip formatter={(v: number) => `$${Math.round(v).toLocaleString()}`} />} />
                              <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                              <Area dataKey="forecast_expenses" name="Expense Forecast" stroke="#ef4444" fill="#ef4444" fillOpacity={0.15} strokeDasharray="5 5" strokeWidth={2} connectNulls={false} type="monotone" />
                              {expenseLastDate && <ReferenceLine x={expenseLastDate} stroke="#6b7280" strokeDasharray="3 3" label={{ value: 'Forecast', fill: '#9ca3af', fontSize: 11, position: 'top' }} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                          )
                          })()
                        ) : (
                          <div className="text-gray-400 text-sm">No daily data available</div>
                        )}
                      </div>

                      {/* Two Column Layout: Categories & Merchants */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Expenses by Category */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="text-white font-semibold">Spending by Category</div>
                            {expenseData.by_category?.length > 5 && (
                              <button
                                onClick={() => setShowAllCategories(!showAllCategories)}
                                className="text-xs text-accent-teal hover:text-accent-teal/80 font-medium transition-colors"
                              >
                                {showAllCategories ? 'Show Less' : `View All (${expenseData.by_category.length})`}
                              </button>
                            )}
                          </div>
                          {expenseData.by_category?.length > 0 ? (
                            <div className="space-y-3">
                              {(showAllCategories ? expenseData.by_category : expenseData.by_category.slice(0, 5)).map((cat: any, idx: number) => {
                                const pct = expenseData.total_expenses > 0 ? (cat.total_amount / expenseData.total_expenses) * 100 : 0
                                const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-pink-500', 'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-teal-500']
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white truncate">{cat.category}</span>
                                      <span className="text-red-400 font-medium">${Math.round(cat.total_amount).toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}% • {cat.transaction_count} transactions</div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No category data</div>
                          )}
                        </div>

                        {/* Top Merchants */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="text-white font-semibold">Top Vendors</div>
                            {expenseData.top_merchants?.length > 5 && (
                              <button
                                onClick={() => setShowAllVendors(!showAllVendors)}
                                className="text-xs text-accent-teal hover:text-accent-teal/80 font-medium transition-colors"
                              >
                                {showAllVendors ? 'Show Less' : `View All (${expenseData.top_merchants.length})`}
                              </button>
                            )}
                          </div>
                          {expenseData.top_merchants?.length > 0 ? (
                            <div className="space-y-3">
                              {(showAllVendors ? expenseData.top_merchants : expenseData.top_merchants.slice(0, 5)).map((merchant: any, idx: number) => {
                                const pct = expenseData.total_expenses > 0 ? (merchant.total_amount / expenseData.total_expenses) * 100 : 0
                                const domain = vendorDomain(merchant.merchant)
                                const initials = merchant.merchant.slice(0, 2).toUpperCase()
                                return (
                                  <div key={idx} className="flex items-center gap-3">
                                    <div className="relative h-8 w-8 flex-shrink-0">
                                      <div data-fallback className="absolute inset-0 rounded-lg bg-dark-card-hover flex items-center justify-center text-xs font-semibold text-gray-400">{initials}</div>
                                      <img
                                        src={`https://logos-api.apistemic.com/domain:${domain}?fallback=404`}
                                        alt=""
                                        className="absolute inset-0 h-8 w-8 rounded-lg object-contain bg-dark-card-hover"
                                        loading="lazy"
                                        onLoad={(e) => {
                                          const img = e.currentTarget
                                          if (img.naturalWidth < 8 || img.naturalHeight < 8) {
                                            img.style.display = 'none'
                                            return
                                          }
                                          const fb = (img.parentElement?.querySelector('[data-fallback]') as HTMLElement | null)
                                          if (fb) fb.style.display = 'none'
                                        }}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-white truncate">{merchant.merchant}</span>
                                        <span className="text-orange-400 font-medium">${Math.round(merchant.total_amount).toLocaleString()}</span>
                                      </div>
                                      <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-orange-500 rounded-full transition-all duration-500"
                                          style={{ width: `${Math.min(pct * 2, 100)}%` }}
                                        />
                                      </div>
                                      <div className="text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}% • {merchant.transaction_count} payments{merchant.accounts?.length > 0 && ` • ${merchant.accounts.join(', ')}`}</div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No vendor data</div>
                          )}
                        </div>
                      </div>

                      {/* Bottom Row: Top Expenses & Peak Days */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top Individual Expenses */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Largest Expenses</div>
                          {expenseData.top_expenses?.length > 0 ? (
                            <div className="space-y-2">
                              {expenseData.top_expenses.slice(0, 5).map((exp: any, idx: number) => {
                                const domain = vendorDomain(exp.name || '')
                                const initials = (exp.name || '??').slice(0, 2).toUpperCase()
                                return (
                                  <div key={idx} className="flex items-center gap-3 p-2 bg-dark-card-hover rounded-lg border border-dark-border">
                                    <div className="relative h-8 w-8 flex-shrink-0">
                                      <div data-fallback className="absolute inset-0 rounded-lg bg-dark-border flex items-center justify-center text-xs font-semibold text-gray-400">{initials}</div>
                                      <img
                                        src={`https://logos-api.apistemic.com/domain:${domain}?fallback=404`}
                                        alt=""
                                        className="absolute inset-0 h-8 w-8 rounded-lg object-contain bg-dark-border"
                                        loading="lazy"
                                        onLoad={(e) => {
                                          const img = e.currentTarget
                                          if (img.naturalWidth < 8 || img.naturalHeight < 8) {
                                            img.style.display = 'none'
                                            return
                                          }
                                          const fb = (img.parentElement?.querySelector('[data-fallback]') as HTMLElement | null)
                                          if (fb) fb.style.display = 'none'
                                        }}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm text-white truncate">{exp.name}</div>
                                      <div className="text-xs text-gray-400">{new Date(exp.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                                    </div>
                                    <div className="text-red-400 font-semibold ml-3">${Math.round(exp.amount).toLocaleString()}</div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No expense data</div>
                          )}
                        </div>

                        {/* Peak Spending Days */}
                        {expenseData.top_expense_days?.length > 0 && (
                          <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                            <div className="text-white font-semibold mb-4">Peak Spending Days</div>
                            <div className="space-y-3">
                              {expenseData.top_expense_days.map((day: any, idx: number) => (
                                <div key={idx} className="bg-dark-card-hover rounded-lg p-4 border border-dark-border">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-lg ${idx === 0 ? 'text-red-400' : idx === 1 ? 'text-orange-400' : 'text-yellow-400'}`}>
                                        {idx === 0 ? '1st' : idx === 1 ? '2nd' : '3rd'}
                                      </span>
                                      <span className="text-white font-medium">
                                        {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </span>
                                    </div>
                                    <div className="text-xl font-bold text-red-400">${Math.round(day.expenses).toLocaleString()}</div>
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">{day.count} transactions</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-10 flex flex-col items-center justify-center gap-4">
                      <div className="text-gray-400 text-lg">No expense data available</div>
                      <div className="text-gray-500 text-sm">Connect a US bank account via Plaid to see deposits, expenses, and balances.</div>
                      <button
                        onClick={() => openPlaidLink()}
                        disabled={!plaidReady}
                        className="mt-2 px-6 py-3 rounded-lg text-sm font-medium bg-accent-teal text-white hover:bg-accent-teal/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Connect Bank Account
                      </button>
                    </div>
                  )}
                  {expenseData && linkToken && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => openPlaidLink()}
                        disabled={!plaidReady}
                        className="px-4 py-2 rounded-lg text-xs font-medium bg-dark-card-hover text-gray-300 border border-dark-border hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Link Another Account
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Census Tab Content */}
              {mainTab === 'Revenue Forecast' && (
                <div className="mt-6">
                  {revenueLoading ? (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">Loading revenue forecast...</div>
                    </div>
                  ) : revenueData ? (
                    <>
                      {/* KPI Cards — Row 1 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                        <StatCard
                          label="Projected 30-Day Revenue"
                          value={formatDollar(revenueData.projected_30day_revenue || 0)}
                          sublabel="Based on current census & rates"
                          tooltip="Net revenue projection using current census, payer reimbursement rates, and historical collection rates"
                        />
                        <StatCard
                          label="Daily Revenue"
                          value={formatDollar(revenueData.daily_revenue || 0)}
                          sublabel="Estimated daily net"
                        />
                        <StatCard
                          label="Billing Rate"
                          value={`${revenueData.billing_rate || 0}%`}
                          sublabel="Patient-days with claims (30d)"
                          tooltip="Of all the days patients were in our facility over the last 30 days, what percentage had a claim submitted to insurance. A low rate means billing is falling behind on submitting claims."
                        />
                        <StatCard
                          label="Collection Rate"
                          value={`${revenueData.collection_rate || 0}%`}
                          sublabel="Collected / Billed (30d)"
                          tooltip="Percentage of billed revenue that was collected via ERA payments in the last 30 days"
                        />
                      </div>
                      {/* KPI Cards — Row 2 */}
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
                        <StatCard
                          label="Daily Claims"
                          value={`${revenueData.avg_daily_claims || 0}`}
                          sublabel="Avg claims / day (30d)"
                          tooltip="Average number of claims submitted per day over the last 30 days."
                        />
                        <StatCard
                          label="Daily Billed"
                          value={formatDollar(revenueData.avg_daily_billed || 0)}
                          sublabel="Avg billed / day (30d)"
                          tooltip="Average amount billed (charged) to insurance per day over the last 30 days."
                        />
                        <StatCard
                          label="Days to Collect"
                          value={`${revenueData.days_to_collect || 0}d`}
                          sublabel="Avg service → payment"
                          tooltip="Average days from service date to ERA payment date (last 90 days). Lower is better for cash flow."
                        />
                        <StatCard
                          label="Denial Rate"
                          value={`${revenueData.denial_rate || 0}%`}
                          sublabel="Claims with $0 paid (90d)"
                          tooltip="Percentage of claims in the last 90 days that received zero payment. Includes pending and denied claims."
                        />
                        <StatCard
                          label="Rev / Patient-Day"
                          value={formatDollar(revenueData.rev_per_patient_day || 0)}
                          sublabel="Collected per census day (30d)"
                          tooltip="Total ERA collections divided by total patient-days from census over the last 30 days. Reflects blended payer mix efficiency."
                        />
                      </div>


                      {/* Revenue Funnel Chart — Expected / Billed / Collected */}
                      <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-white font-semibold">Revenue Funnel</div>
                            <div className="text-xs text-gray-400">Potential (census) → Billed (claims) → Collected (ERA) + 30-day forecast</div>
                          </div>
                        </div>
                        {revenueData.revenue_timeseries?.length > 0 ? (
                          (() => {
                          // Compute stacked bars: collected + pending (billed - collected) = total billed
                          const withPending = revenueData.revenue_timeseries.map((d: any) => ({
                            ...d,
                            billed_pending: Math.max(0, (d.billed || 0) - (d.collected || 0)),
                          }))
                          const { data: revenueChartData, lastHistoricalDate: revenueLastDate } = prepareForecastData(withPending, ['potential'])
                          return (
                          <ResponsiveContainer width="100%" height={320}>
                            <ComposedChart data={revenueChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                              <XAxis
                                dataKey="date"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              />
                              <YAxis
                                yAxisId="left"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickFormatter={(v) => formatDollar(v)}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                label={{ value: 'Claims', angle: 90, position: 'insideRight', fill: '#9ca3af', fontSize: 10 }}
                              />
                              <Tooltip content={<ForecastTooltip formatter={(v: number, name: string) => name === 'Daily Claims' ? `${v}` : `$${Math.round(v).toLocaleString()}`} />} />
                              <Legend />
                              <Bar yAxisId="left" dataKey="collected" name="Collected (ERA)" fill="#14b8a6" stackId="billed" radius={[0, 0, 0, 0]} />
                              <Bar yAxisId="left" dataKey="billed_pending" name="Awaiting Payment" fill="#3b82f6" stackId="billed" radius={[3, 3, 0, 0]} />
                              <Line yAxisId="right" dataKey="claims_count" name="Daily Claims" stroke="#f59e0b" strokeWidth={1.5} dot={false} type="monotone" />
                              <Area yAxisId="left" dataKey="forecast_potential" name="Potential Forecast" stroke="#22c55e" fill="#22c55e" fillOpacity={0.1} strokeDasharray="5 5" strokeWidth={2} connectNulls={false} type="monotone" />
                              {revenueLastDate && <ReferenceLine yAxisId="left" x={revenueLastDate} stroke="#6b7280" strokeDasharray="3 3" label={{ value: 'Forecast', fill: '#9ca3af', fontSize: 11, position: 'top' }} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                          )
                          })()
                        ) : (
                          <div className="text-gray-400 text-sm">No revenue data available</div>
                        )}
                      </div>

                      {/* Two Column: Revenue by Payer + Revenue by Facility */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Revenue by Payer */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Revenue by Payer</div>
                          {revenueData.by_payer?.length > 0 ? (
                            <div className="space-y-3">
                              {revenueData.by_payer.map((p: any, idx: number) => {
                                const maxNet = revenueData.by_payer[0]?.monthly_net || 1
                                const pct = (p.monthly_net / maxNet) * 100
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white truncate">{p.payer}</span>
                                      <span className="text-green-400 font-medium">{formatDollar(p.monthly_net)}/mo</span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                      <span>{p.patients} patients</span>
                                      <span>Rate: {formatDollar(p.daily_rate)}/day</span>
                                      <span>Collection: {(p.collection_rate * 100).toFixed(1)}%</span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No payer data</div>
                          )}
                        </div>

                        {/* Revenue by Facility */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Revenue by Facility</div>
                          {revenueData.by_facility?.length > 0 ? (
                            <div className="space-y-4">
                              {revenueData.by_facility.map((f: any, idx: number) => (
                                <div key={idx} className="bg-dark-card-hover border border-dark-border rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-white font-medium">{f.facility}</span>
                                    <span className="text-green-400 font-semibold">{formatDollar(f.monthly_net)}/mo</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                                    <span>{f.patients} patients</span>
                                    <span>Capacity: {f.capacity}</span>
                                  </div>
                                  <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${f.utilization > 80 ? 'bg-red-500' : f.utilization > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                      style={{ width: `${Math.min(f.utilization, 100)}%` }}
                                    />
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">{f.utilization}% utilization</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No facility data</div>
                          )}
                        </div>
                      </div>

                      {/* Scenario Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {revenueData.scenarios && Object.entries(revenueData.scenarios).map(([key, s]: [string, any]) => {
                          const borderColor = key === 'best' ? 'border-green-500' : key === 'worst' ? 'border-red-500' : 'border-dark-border'
                          const textColor = key === 'best' ? 'text-green-400' : key === 'worst' ? 'text-red-400' : 'text-accent-teal'
                          return (
                            <div key={key} className={`bg-dark-card border-2 ${borderColor} rounded-xl p-5`}>
                              <div className="text-sm text-gray-400 mb-1">{s.label}</div>
                              <div className={`text-2xl font-semibold ${textColor}`}>{formatDollar(s.monthly_revenue)}</div>
                              <div className="text-xs text-gray-500 mt-1">30-day projection</div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Weekly Projection Table */}
                      {revenueData.weekly_projection?.length > 0 && (
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6">
                          <div className="text-white font-semibold mb-4">Weekly Revenue Projection</div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-400 border-b border-dark-border">
                                  <th className="py-2 pr-4 font-medium">Week</th>
                                  <th className="py-2 pr-4 font-medium">Census</th>
                                  <th className="py-2 pr-4 font-medium">Weekly Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {revenueData.weekly_projection.map((w: any) => (
                                  <tr key={w.week} className="border-b border-dark-border">
                                    <td className="py-2 pr-4 text-white">Week {w.week}</td>
                                    <td className="py-2 pr-4 text-gray-300">{w.census}</td>
                                    <td className="py-2 pr-4 text-green-400 font-medium">{formatDollar(w.weekly_revenue)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">No revenue data available</div>
                    </div>
                  )}
                </div>
              )}

              {mainTab === 'Census' && (
                <div className="mt-6">
                  {censusLoading ? (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">Loading census data...</div>
                    </div>
                  ) : censusAnalytics ? (
                    <>
                      {/* Executive Summary KPIs */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
                        <StatCard
                          label="Total Census"
                          value={censusAnalytics.total_census || 0}
                          change={(censusAnalytics.total_census || 0) - (censusAnalytics.prev_total_census || 0)}
                          changeLabel="vs last week"
                          sublabel="Active patients"
                        />
                        <StatCard
                          label="Approved"
                          value={censusAnalytics.total_approved || 0}
                          change={(censusAnalytics.total_approved || 0) - (censusAnalytics.prev_total_approved || 0)}
                          changeLabel="vs last week"
                          sublabel={`${censusAnalytics.total_census > 0 ? Math.round((censusAnalytics.total_approved / censusAnalytics.total_census) * 100) : 0}% of census`}
                        />
                        <StatCard
                          label="Pending Auth"
                          value={censusAnalytics.total_pending || 0}
                          sublabel="Awaiting approval"
                        />
                        <StatCard
                          label="Avg Length of Stay"
                          value={`${Math.round(censusAnalytics.avg_length_of_stay || 0)} days`}
                          sublabel="All facilities"
                        />
                        <StatCard
                          label="Admissions This Week"
                          value={censusAnalytics.admissions_this_week || 0}
                          change={(censusAnalytics.admissions_this_week || 0) - (censusAnalytics.admissions_last_week || 0)}
                          changeLabel="vs last week"
                        />
                      </div>

                      {/* Narrative Insight */}
                      <div className="bg-gradient-to-r from-dark-card to-dark-card-hover border border-dark-border rounded-xl p-5 mb-6">
                        <div>
                          <div className="text-white font-semibold mb-1">Census Summary</div>
                          <div className="text-sm text-gray-300 leading-relaxed">
                            {censusAnalytics.total_census > 0 ? (
                              <>
                                Currently <span className="text-accent-teal font-medium">{censusAnalytics.total_census} patients</span> across {censusAnalytics.by_facility?.length || 0} facilities.
                                {censusAnalytics.by_facility?.[0] && (
                                  <> <span className="text-white font-medium">{censusAnalytics.by_facility[0].facility}</span> leads with {censusAnalytics.by_facility[0].total_patients} patients. </>
                                )}
                                {censusAnalytics.total_approved > 0 && (
                                  <><span className="text-green-400 font-medium">{censusAnalytics.total_approved} ({Math.round((censusAnalytics.total_approved / censusAnalytics.total_census) * 100)}%)</span> are authorized</>
                                )}
                                {censusAnalytics.total_pending > 0 && (
                                  <> and <span className="text-yellow-400 font-medium">{censusAnalytics.total_pending}</span> are pending authorization</>
                                )}.
                                {censusAnalytics.admissions_this_week > 0 && (
                                  <> This week saw <span className="text-blue-400 font-medium">{censusAnalytics.admissions_this_week} new admissions</span>.</>
                                )}
                              </>
                            ) : (
                              'No active census data available.'
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Admissions Trend Chart */}
                      <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-white font-semibold">Admissions Trend</div>
                            <div className="text-xs text-gray-400">New patient admissions over time</div>
                          </div>
                        </div>
                        {censusAnalytics.admissions_timeseries?.length > 0 ? (
                          (() => {
                          const { data: censusChartData, lastHistoricalDate: censusLastDate } = prepareForecastData(censusAnalytics.admissions_timeseries, ['admissions', 'axis', 'beachside', 'affinity'])
                          return (
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={censusChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                              <XAxis
                                dataKey="date"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              />
                              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                              <Tooltip content={<ForecastTooltip />} />
                              <Legend />
                              <Bar dataKey="axis" name="Axis" fill="#14b8a6" radius={[4, 4, 0, 0]} stackId="admissions" />
                              <Bar dataKey="beachside" name="Beachside" fill="#3b82f6" radius={[4, 4, 0, 0]} stackId="admissions" />
                              <Bar dataKey="affinity" name="Affinity" fill="#8b5cf6" radius={[4, 4, 0, 0]} stackId="admissions" />
                              <Area dataKey="forecast_admissions" name="Admissions Forecast" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.15} strokeDasharray="5 5" strokeWidth={2} connectNulls={false} type="monotone" />
                              {censusLastDate && <ReferenceLine x={censusLastDate} stroke="#6b7280" strokeDasharray="3 3" label={{ value: 'Forecast', fill: '#9ca3af', fontSize: 11, position: 'top' }} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                          )
                          })()
                        ) : (
                          <div className="text-gray-400 text-sm">No admission data available for this period</div>
                        )}
                      </div>

                      {/* Two Column Layout: Facility Breakdown & Level of Care */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Census by Facility */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Census by Facility</div>
                          {censusAnalytics.by_facility?.length > 0 ? (
                            <div className="space-y-4">
                              {censusAnalytics.by_facility.map((fac: any, idx: number) => {
                                const pct = censusAnalytics.total_census > 0 ? (fac.total_patients / censusAnalytics.total_census) * 100 : 0
                                const colors = ['bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500']
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white font-medium">{fac.facility}</span>
                                      <span className="text-accent-teal font-semibold">{fac.total_patients}</span>
                                    </div>
                                    <div className="h-3 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                      <span>RTC: {fac.rtc}</span>
                                      <span>PHP: {fac.php}</span>
                                      <span>Detox: {fac.detox}</span>
                                      <span>IOP: {fac.iop}</span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No facility data</div>
                          )}
                        </div>

                        {/* Level of Care Distribution */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Level of Care Distribution</div>
                          {censusAnalytics.by_level_of_care?.length > 0 ? (
                            <div className="space-y-3">
                              {censusAnalytics.by_level_of_care.map((loc: any, idx: number) => {
                                const pct = censusAnalytics.total_census > 0 ? (loc.count / censusAnalytics.total_census) * 100 : 0
                                const colors: Record<string, string> = {
                                  'RTC': 'bg-blue-500',
                                  'PHP': 'bg-orange-500',
                                  'Detox': 'bg-teal-500',
                                  'IOP': 'bg-violet-500',
                                  'Other': 'bg-gray-500'
                                }
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white">{loc.level_of_care}</span>
                                      <span className="text-gray-300">{loc.count} <span className="text-gray-500">({pct.toFixed(0)}%)</span></span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className={`h-full ${colors[loc.level_of_care] || 'bg-gray-500'} rounded-full transition-all duration-500`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No level of care data</div>
                          )}
                        </div>
                      </div>

                      {/* Two Column Layout: Payer Breakdown & Length of Stay */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Payer Breakdown */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Insurance Breakdown</div>
                          {censusAnalytics.by_payer?.length > 0 ? (
                            <div className="space-y-3">
                              {censusAnalytics.by_payer.slice(0, 6).map((payer: any, idx: number) => {
                                const pct = censusAnalytics.total_census > 0 ? (payer.patient_count / censusAnalytics.total_census) * 100 : 0
                                return (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-white truncate">{payer.payer}</span>
                                      <span className="text-blue-400 font-medium">{payer.patient_count}</span>
                                    </div>
                                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}% of census</div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No payer data</div>
                          )}
                        </div>

                        {/* Length of Stay by Facility */}
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Length of Stay by Facility</div>
                          {censusAnalytics.length_of_stay?.length > 0 ? (
                            <div className="space-y-3">
                              {censusAnalytics.length_of_stay.map((los: any, idx: number) => (
                                <div key={idx} className="bg-dark-card-hover rounded-lg p-4 border border-dark-border">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-white font-medium">{los.facility}</span>
                                    <span className="text-2xl font-bold text-accent-teal">{Math.round(los.avg_los)} days</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-400">
                                    <span>Min: {los.min_los} days</span>
                                    <span>Max: {los.max_los} days</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">No length of stay data</div>
                          )}
                        </div>
                      </div>

                      {/* Recent Admissions */}
                      {censusAnalytics.recent_admissions?.length > 0 && (
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-white font-semibold mb-4">Recent Admissions (Last 7 Days)</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {censusAnalytics.recent_admissions.map((admission: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-dark-card-hover rounded-lg border border-dark-border">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-white font-medium truncate">{admission.patient_name}</div>
                                  <div className="text-xs text-gray-400">{admission.facility} • {admission.level_of_care}</div>
                                </div>
                                <div className="text-right ml-3">
                                  <div className="text-sm text-accent-teal font-medium">
                                    {new Date(admission.admission_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </div>
                                  <div className="text-xs text-gray-400 truncate max-w-[100px]">{admission.insurance}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Claims Breakdown */}
                      {censusClaimsLoading ? (
                        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                          <div className="text-gray-400 animate-pulse">Loading claims data...</div>
                        </div>
                      ) : censusClaims ? (
                        <div className="space-y-6">
                          <div className="text-white font-semibold text-lg">Claims Overview</div>

                          {/* Claims KPI Cards */}
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <StatCard label="Total Claims" value={censusClaims.total_claims} change={censusClaims.this_week_claims - censusClaims.last_week_claims} changeLabel="vs last week" />
                            <StatCard label="Total Billed" value={formatDollar(censusClaims.total_billed)} change={censusClaims.this_week_billed - censusClaims.last_week_billed} changeLabel="vs last week" changeType="currency" />
                            <StatCard label="Total Paid" value={formatDollar(censusClaims.total_paid)} change={censusClaims.this_week_paid - censusClaims.last_week_paid} changeLabel="vs last week" changeType="currency" />
                            <StatCard label="Outstanding" value={formatDollar(censusClaims.total_balance)} />
                            <StatCard label="Collection Rate" value={`${censusClaims.collection_rate}%`} tooltip="Paid / Billed amount" />
                            <StatCard label="Write-Offs" value={censusClaims.write_off_count} inverseColors />
                          </div>

                          {/* Claims Status Summary */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                              { label: 'Paid', count: censusClaims.paid_count, color: 'text-green-400' },
                              { label: 'Partial Paid', count: censusClaims.partial_paid_count, color: 'text-yellow-400' },
                              { label: 'At Insurance', count: censusClaims.pending_count, color: 'text-blue-400' },
                              { label: 'Balance Due', count: censusClaims.balance_due_count, color: 'text-orange-400' },
                              { label: 'Write-Off', count: censusClaims.write_off_count, color: 'text-red-400' },
                            ].map((s, i) => (
                              <div key={i} className="bg-dark-card-hover border border-dark-border rounded-lg p-3 text-center">
                                <div className={`text-2xl font-bold ${s.color}`}>{s.count}</div>
                                <div className="text-xs text-gray-400 mt-1">{s.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* By Payer */}
                          {censusClaims.by_payer?.length > 0 && (
                            <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                              <div className="text-white font-semibold mb-4">Claims by Payer</div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-400 border-b border-dark-border">
                                      <th className="py-2 pr-4 font-medium">Payer</th>
                                      <th className="py-2 pr-4 font-medium text-right">Claims</th>
                                      <th className="py-2 pr-4 font-medium text-right">Billed</th>
                                      <th className="py-2 pr-4 font-medium text-right">Paid</th>
                                      <th className="py-2 pr-4 font-medium text-right">Balance</th>
                                      <th className="py-2 pr-4 font-medium text-right">Collection %</th>
                                      <th className="py-2 font-medium text-right">Write-Offs</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {censusClaims.by_payer.map((row: any, idx: number) => (
                                      <tr key={idx} className="border-b border-dark-border/50 hover:bg-dark-card-hover">
                                        <td className="py-2.5 pr-4 text-white font-medium">{row.payer}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-300">{row.claim_count}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-300">{formatDollar(row.billed)}</td>
                                        <td className="py-2.5 pr-4 text-right text-green-400">{formatDollar(row.paid)}</td>
                                        <td className="py-2.5 pr-4 text-right text-orange-400">{formatDollar(row.balance)}</td>
                                        <td className="py-2.5 pr-4 text-right">
                                          <span className={row.collection_rate >= 70 ? 'text-green-400' : row.collection_rate >= 40 ? 'text-yellow-400' : 'text-red-400'}>
                                            {row.collection_rate}%
                                          </span>
                                        </td>
                                        <td className="py-2.5 text-right text-red-400">{row.write_offs > 0 ? row.write_offs : '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* By Level of Care */}
                            {censusClaims.by_loc?.length > 0 && (
                              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                                <div className="text-white font-semibold mb-4">Claims by Level of Care</div>
                                <div className="space-y-3">
                                  {censusClaims.by_loc.map((row: any, idx: number) => {
                                    const pct = censusClaims.total_billed > 0 ? (row.billed / censusClaims.total_billed * 100) : 0
                                    return (
                                      <div key={idx}>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-sm text-white font-medium">{row.loc}</span>
                                          <span className="text-sm text-gray-300">{row.claim_count} claims • {formatDollar(row.billed)}</span>
                                        </div>
                                        <div className="w-full bg-dark-card-hover rounded-full h-2">
                                          <div className="bg-accent-teal rounded-full h-2" style={{ width: `${Math.min(pct, 100)}%` }} />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                                          <span>Paid: {formatDollar(row.paid)}</span>
                                          <span>Balance: {formatDollar(row.balance)}</span>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}

                            {/* By Facility */}
                            {censusClaims.by_facility?.length > 0 && (
                              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                                <div className="text-white font-semibold mb-4">Claims by Facility</div>
                                <div className="space-y-3">
                                  {censusClaims.by_facility.map((row: any, idx: number) => {
                                    const pct = censusClaims.total_billed > 0 ? (row.billed / censusClaims.total_billed * 100) : 0
                                    return (
                                      <div key={idx}>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-sm text-white font-medium">{row.facility}</span>
                                          <span className="text-sm text-gray-300">{row.claim_count} claims • {formatDollar(row.billed)}</span>
                                        </div>
                                        <div className="w-full bg-dark-card-hover rounded-full h-2">
                                          <div className="bg-purple-500 rounded-full h-2" style={{ width: `${Math.min(pct, 100)}%` }} />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                                          <span>Paid: {formatDollar(row.paid)}</span>
                                          <span>Write-offs: {row.write_offs}</span>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Write-Offs by Payer */}
                          {censusClaims.write_offs_by_payer?.length > 0 && (
                            <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                              <div className="text-white font-semibold mb-4">Write-Offs by Payer</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {censusClaims.write_offs_by_payer.map((row: any, idx: number) => (
                                  <div key={idx} className="bg-dark-card-hover border border-dark-border rounded-lg p-4">
                                    <div className="text-sm text-white font-medium mb-2">{row.payer}</div>
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-xs text-gray-400">{row.claim_count} claims</div>
                                        <div className="text-xs text-gray-400">Billed: {formatDollar(row.billed)}</div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-red-400">{formatDollar(row.written_off)}</div>
                                        <div className="text-xs text-gray-500">written off</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Claims by Status */}
                          {censusClaims.by_status?.length > 0 && (
                            <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                              <div className="text-white font-semibold mb-4">Claims by Status</div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-400 border-b border-dark-border">
                                      <th className="py-2 pr-4 font-medium">Status</th>
                                      <th className="py-2 pr-4 font-medium text-right">Claims</th>
                                      <th className="py-2 pr-4 font-medium text-right">Billed</th>
                                      <th className="py-2 font-medium text-right">Paid</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {censusClaims.by_status.map((row: any, idx: number) => (
                                      <tr key={idx} className="border-b border-dark-border/50 hover:bg-dark-card-hover">
                                        <td className="py-2.5 pr-4 text-white font-medium">{row.status}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-300">{row.claim_count}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-300">{formatDollar(row.billed)}</td>
                                        <td className="py-2.5 text-right text-green-400">{formatDollar(row.paid)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}

                    </>
                  ) : (
                    <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                      <div className="text-gray-400">No census data available</div>
                    </div>
                  )}
                </div>
              )}

              {/* Geo-Analytics Tab Content */}
              {mainTab === 'Marketing' && (
                <div className="mt-6">
                  <GeoAnalyticsSection
                    from={(() => {
                      const d = dashboardStartDate
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    })()}
                    to={(() => {
                      const d = dashboardEndDate
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    })()}
                    rangeLabel={dashboardRangeLabel}
                  />
                </div>
              )}
            </div>

          {/* Legacy graphs removed (kept in code history, not rendered) */}
          {false && (
            <>
          <DisplayAccordianHeader label={'Status'} display={showStatus} onChange={() => {setShowStatus(!showStatus)}}/>
          {
            showStatus
              ? <div style={{marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div className="h-10 mt-3 px-2 w-full flex flex-col justify-center bg-stone-200 dark:bg-stone-700 rounded-md mb-4">
                    <div className='w-full flex flex-row items-center justify-center gap-5'>
                      <div className='flex flex-row text-black dark:text-white items-center'>
                        <p className='text-md mr-3'>Start Date:</p>
                        <CalendarSelectComponent selectedDate={statusStartDate} handleDateChange={setStatusStartDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>End Date:</p>
                        <CalendarSelectComponent selectedDate={statusEndDate} handleDateChange={setStatusEndDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>Table:</p>
                        <SelectOptionComponent  options={statusOPtions} value={selectedStatus} onChange={setSelectedStatus}/>
                      </div>
                    </div>
                </div>
                  <h1 className='text-3xl text-black dark:text-white font-bold mt-8 mb-3'>Status</h1>
                  <BarChart
                    width={window.innerWidth * .75}
                    height={((2/3) * (window.innerWidth * .25))}
                    data={analyticsData}
                    margin={{
                      top: 5,
                      right: 150,
                      left: 100,
                      bottom: 5
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="grey" />
                    <XAxis dataKey="date"  tick={{ fill: 'grey' }} />
                    <YAxis tick={{fill: 'grey'}}/>
                    <Tooltip />
                    <Legend />
                    {
                      selectedStatus === 'Pending Approval'
                        ? <Bar
                            strokeWidth={2}
                            type="monotone"
                            dataKey="Pending Approval"
                            fill="#fcba03"
                            label="Pending Approval"
                          />
                        : selectedStatus === 'Total Approved'
                            ? <Bar
                                strokeWidth={2}
                                type="monotone"
                                dataKey="Total Approved"
                                fill="#fc8003"
                                label="Total Approved"
                              />
                            : selectedStatus === 'Denied'
                                ? <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="Denied"
                                    fill="red"
                                    label="Denied"
                                  />
                                : <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="Private Pay"
                                    fill="#fcd303"
                                    label="Private Pay"
                                  />
                    }
                    
                  </BarChart>
                  <TotalsPills data={analyticsData} keysAndLabels={keysAndLabelsStatus} colors={colors} />
                </div>
              : null
          }
          <DisplayAccordianHeader label={'Status Approved'} display={showApproved} onChange={() => {setShowApproved(!showApproved)}}/>
          {
            showApproved
              ? <div style={{marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div className="h-10 mt-3 px-2 w-full flex flex-col justify-center bg-stone-200 dark:bg-stone-700 rounded-md mb-4">
                  <div className='w-full flex flex-row items-center justify-center gap-5'>
                      <div className='flex flex-row text-black dark:text-white items-center'>
                        <p className='text-md mr-3'>Start Date:</p>
                        <CalendarSelectComponent selectedDate={statusStartDate} handleDateChange={setStatusStartDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>End Date:</p>
                        <CalendarSelectComponent selectedDate={statusEndDate} handleDateChange={setStatusEndDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>Table:</p>
                        <SelectOptionComponent  options={approvedOptions} value={selectedApproved} onChange={setSelectedApproved}/>
                      </div>
                    </div>
                </div>
                  <h1 className='text-3xl text-black dark:text-white font-bold mt-8 mb-3'>Approved Status</h1>
                  <BarChart
                    width={window.innerWidth * .75}
                    height={((2/3) * (window.innerWidth * .25))}
                    data={analyticsData}
                    margin={{
                      top: 5,
                      right: 150,
                      left: 100,
                      bottom: 5
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="grey" />
                    <XAxis dataKey="date"  tick={{ fill: 'grey' }} />
                    <YAxis tick={{ fill: 'grey' }} />
                    <Tooltip />
                    <Legend />
                    {
                      selectedApproved === 'Approved'
                        ? <Bar
                            strokeWidth={2}
                            type="monotone"
                            dataKey="Approved"
                            fill="#fcba03"
                            label="Approved"
                          />
                        : selectedApproved === 'Approved No RTC'
                            ? <Bar
                                strokeWidth={2}
                                type="monotone"
                                dataKey="Approved No RTC"
                                fill="#fc8003"
                                label="Approved No RTC"
                              />
                            : selectedApproved === 'Approved LB No RTC'
                                ? <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="Approved LB No RTC"
                                    fill="red"
                                    label="Approved LB No RTC"
                                  />
                                : <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="Approved DB"
                                    fill="#fcd303"
                                    label="Approved DB"
                                  />
                    }
                  </BarChart>
                  <TotalsPills data={analyticsData} keysAndLabels={keysAndLabelsApproved} colors={colors} />
                </div>
              : null
          }
          <DisplayAccordianHeader label={'Checked In'} display={showCheckedIn} onChange={() => {setShowCheckedIn(!showCheckedIn)}}/>
          {
            showCheckedIn
              ? <div style={{marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div className="h-10 mt-3 px-2 w-full flex flex-col justify-center bg-stone-200 dark:bg-stone-700 rounded-md mb-4">
                    <div className='w-full flex flex-row items-center justify-center gap-5'>
                      <div className='flex flex-row text-black dark:text-white items-center'>
                        <p className='text-md mr-3'>Start Date:</p>
                        <CalendarSelectComponent selectedDate={statusStartDate} handleDateChange={setStatusStartDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>End Date:</p>
                        <CalendarSelectComponent selectedDate={statusEndDate} handleDateChange={setStatusEndDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>Table:</p>
                        <SelectOptionComponent  options={checkedInOptions} value={selectedCheckedIn} onChange={setSelectedCheckedIn}/>
                      </div>
                    </div>
                </div>
                  <h1 className='text-3xl text-black dark:text-white font-bold mt-8 mb-3'>Checked In</h1>
                  <BarChart
                    width={window.innerWidth * .75}
                    height={((2/3) * (window.innerWidth * .25))}
                    data={analyticsData}
                    margin={{
                      top: 5,
                      right: 150,
                      left: 100,
                      bottom: 5
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="grey" />
                    <XAxis dataKey="date"  tick={{ fill: 'grey' }} />
                    <YAxis tick={{ fill: 'grey' }} />
                    <Tooltip />
                    <Legend />
                    {
                      selectedCheckedIn === 'Arrived'
                        ? <Bar
                            strokeWidth={2}
                            type="monotone"
                            dataKey="Arrived"
                            fill="#fcba03"
                            label="Arrived"
                          />
                        : selectedCheckedIn === 'Pending Arrival'
                            ? <Bar
                                strokeWidth={2}
                                type="monotone"
                                dataKey="Pending Arrival"
                                fill="#fc8003"
                                label="Arrived"
                              />
                            : selectedCheckedIn === 'MIA'
                                ? <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="MIA"
                                    fill="red"
                                    label="MIA"
                                  />
                                : <Bar
                                    strokeWidth={2}
                                    type="monotone"
                                    dataKey="BD"
                                    fill="#fcd303"
                                    label="BD"
                                  />
                    }
                  </BarChart>
                  <TotalsPills data={analyticsData} keysAndLabels={keysAndLabelsCheckedIn} colors={colors}/>
                </div>
              : null
          }
          <DisplayAccordianHeader label={'Census'} display={showCensus} onChange={() => {setShowCensus(!showCensus)}}/>
          {
            showCensus
              ? <div style={{marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h1 className='text-3xl text-black dark:text-white font-bold mt-8 mb-3'>Census</h1>
                  <BarChart
                    width={window.innerWidth * 0.65}
                    height={((2 / 3) * (window.innerWidth * 0.25))}
                    data={censusData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="grey" />
                    <XAxis dataKey="facility" tick={{ fill: 'grey' }} />
                    <YAxis tick={{ fill: 'grey' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Total Patients" fill="red" activeBar={<Rectangle fill="red" stroke="red" />}/>
                    <Bar dataKey="New Admits" fill="green" activeBar={<Rectangle fill="green" stroke="green" />}/>
                    <Bar dataKey="RTC" fill="blue" activeBar={<Rectangle fill="blue" stroke="blue" />}/>
                    <Bar dataKey="Detox" fill="teal" activeBar={<Rectangle fill="teal" stroke="teal" />}/>
                    <Bar dataKey="PHP" fill="orange" activeBar={<Rectangle fill="orange" stroke="orange" />}/>
                    <Bar dataKey="IOP" fill="violet" activeBar={<Rectangle fill="violet" stroke="violet" />}/>
                  </BarChart>
                  <TotalsPills data={censusData} keysAndLabels={keysAndLabelsCensus} colors={colors}/>
                </div>
              : null
          }
          <DisplayAccordianHeader label={'Anticipated Discharge'} display={showAnticipated} onChange={() => {setShowAnticipated(!showAnticipated)}}/>
          {
            showAnticipated
              ? <div style={{marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <h1 className='text-3xl text-black dark:text-white font-bold mt-8 mb-3'>Anticipated Discharge Data</h1>
              <BarChart
                width={window.innerWidth * 0.65}
                height={((2 / 3) * (window.innerWidth * 0.25))}
                data={anticipatedDischargeData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="grey" />
                <XAxis dataKey="Date" tick={{ fill: 'grey' }} />
                <YAxis tick={{ fill: 'grey' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Axis" fill="blue" activeBar={<Rectangle fill="blue" stroke="blue" />}/>
                <Bar dataKey="Affinity" fill="red" activeBar={<Rectangle fill="red" stroke="red" />}/>
                <Bar dataKey="Beachside" fill="green" activeBar={<Rectangle fill="green" stroke="green" />}/>
              </BarChart>
              <TotalsPills data={anticipatedDischargeData} keysAndLabels={keysAndLabelsAnticipatedDischarge} colors={colors}/>
            </div>
          : null
          }
          <DisplayAccordianHeader label={'Conversion'} display={showConversion} onChange={() => { setShowConversion(!showConversion); filterConversionByTimeCustom(pieStartDate, pieEndDate)}} />
          {
            showConversion
              ? <div className='max-width flex flex-col overflow-auto'>
                <div className="h-10 mt-3 px-2 w-full flex flex-col justify-center bg-stone-200 dark:bg-stone-700 rounded-md mb-4">
                  <div className='w-full flex flex-row items-center justify-center gap-5'>
                      <div className='flex flex-row text-black dark:text-white items-center'>
                        <p className='text-md mr-3'>Start Date:</p>
                        <CalendarSelectComponent selectedDate={pieStartDate} handleDateChange={setPieStartDate}/>
                      </div>
                      <div className='flex flex-row text-black dark:text-white m-1  items-center'>
                        <p className='text-md mr-3'>End Date:</p>
                        <CalendarSelectComponent selectedDate={pieEndDate} handleDateChange={setPieEndDate}/>
                      </div>
                    </div>
                    {/* <div className='w-full flex flex-row items-center justify-start'>
                      <SelectOptionComponent value={pieTime} onChange={handlePieTimeChange} options={['1 Day','1 Week', '1 Month', '3 Months', '6 Months']} />
                    </div> */}
                  </div>

                    {(() => {
                      const sortedOverallPieData = overallPieData.sort((a: any, b: any) => a.name.localeCompare(b.name));
                      const sortedApprovedPieData = pieApprovedData.sort((a: any, b: any) => a.name.localeCompare(b.name));
                      const sortedBookedPieData = pieBookedData.sort((a: any, b: any) => a.name.localeCompare(b.name));


                      return (
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mt-6'>
  {/* Overall Data Chart */}
  <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200'>
    <div className='flex items-center gap-3 mb-6'>
      <div className='w-1 h-8 bg-primary dark:bg-secondary rounded-full' />
      <h3 className='text-gray-900 dark:text-white font-semibold text-lg'>Overall Data</h3>
    </div>
    <ResponsiveContainer width='100%' height={350}>
      <PieChart>
        <Pie
          data={sortedOverallPieData}
          dataKey='value'
          nameKey='name'
          cx='50%'
          cy='50%'
          innerRadius={80}
          outerRadius={120}
          paddingAngle={2}
          label={renderOverallCustomizedLabel}
          labelLine={false}
        >
          {sortedOverallPieData.map((entry: any, index: any) => (
            <Cell
              key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]}
              className='transition-opacity duration-200 hover:opacity-80'
            />
          ))}
        </Pie>
        <Tooltip content={customTooltipOverall} />
        <Legend
          verticalAlign="bottom"
          height={36}
          iconType="circle"
          wrapperStyle={{ paddingTop: '20px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  </div>

  {/* Conversion Rate Chart */}
  <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200'>
    <div className='flex items-center gap-3 mb-6'>
      <div className='w-1 h-8 bg-primary dark:bg-secondary rounded-full' />
      <h3 className='text-gray-900 dark:text-white font-semibold text-lg'>Conversion Rate by Coordinator</h3>
    </div>
    <ResponsiveContainer width="100%" height={500}>
      <PieChart>
        <Pie
          data={sortedBookedPieData}
          dataKey='value'
          nameKey='name'
          cx='50%'
          cy='45%'
          innerRadius={90}
          outerRadius={140}
          paddingAngle={3}
          label={renderPieCustomizedLabel}
          labelLine={false}
        >
          {sortedBookedPieData.map((entry: any, index: any) => {
            const color = COLORS2[entry.name]
            return (
            <Cell
              key={`cell-${index}`}
              fill={color || COLORS[index % COLORS.length]}
              className='transition-opacity duration-200 hover:opacity-80'
            />
          )})}
        </Pie>
        <Tooltip content={customTooltipConversionRate}/>
        <Legend
          verticalAlign="bottom"
          height={80}
          iconType="circle"
          wrapperStyle={{ paddingTop: '30px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  </div>
</div>
                      );
                    })()}
                </div>
              : null
          }
            </>
          )}
          </div>
        </div>
  } // Render your custom content component here
    />
  )
}

export default IntakeAnalytivsScreen

